import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isChatTranslationPending, messageBodyForViewer } from '../../constants/chatLanguages';
import ReactQuill from 'react-quill';
import ImageViewerModal from './ImageViewerModal';
import AttachmentComposeModal from './AttachmentComposeModal';
import AttachmentGroupBar from './AttachmentGroupBar';
import ReactionPicker from './ReactionPicker';
import MessageReactionBadge from './MessageReactionBadge';
import { partitionMessageReactions, getMessageWorkflowVisual, WORKFLOW_CLOSED_STATUS_ID, WORK_ITEM_TYPE_TASK_ID, getActiveWorkflowByCategory, getReactionDisplay, formatReactionToMessageLine, messageHasUrgentWorkflow, messageIsRaisedWorkflow, messageIsTaskWorkItem, mergeReactionsWithWorkflowCategory, canonicalReactionId } from '../../utils/reactionConfig';
import UrgentCountdownBadge from './UrgentCountdownBadge';
import UrgentTag, { useUrgentBlink } from './UrgentTag';
import { updateWorkflowTrack, addMessageCustomTag, removeMessageCustomTag, assignWorkflowMessage, updateMessage } from '../../store/slices/chatSlice';
import AttachmentsListModal from './AttachmentsListModal';
import TimelineLinkCard from './TimelineLinkCard';
import UserProfileModal from '../common/UserProfileModal';
import ConfirmModal from '../common/ConfirmModal';
import CreateNoteDialog from './CreateNoteDialog';
import MeetingTimelineCard from './MeetingTimelineCard';
import AppointmentTimelineCard from './AppointmentTimelineCard';
import EventTimelineCard from './EventTimelineCard';
import WorkItemShareCard from '../common/WorkItemShareCard';
import { copyTextToClipboard } from '../../utils/copyText';
import { buildWorkItemShareUrl, resolveWorkItemShare, withWorkItemShareTitle } from '../../utils/workItemShare';
import { buildChatThreadShareUrl, resolveChatThreadShare } from '../../utils/chatThreadShare';
import { getTopLevelParentId } from '../../utils/threadUtils';
import AttendanceTimelineButton from '../attendance/AttendanceTimelineButton';
import { getMeetingNoteMessages, isCallNoteMessage, isMeetingActive, isStandaloneTimelineCard } from '../../utils/meetingUtils';
import { isLocalOnlyMessage, getMessageTickState } from '../../utils/messageDeliveryStatus';
import { LiveCallBubbleBackdrop } from '../../hooks/useLiveCallBubbleBackground';
import WorkflowTrackDialog from './WorkflowTrackDialog';
import WorkflowViewDialog from './WorkflowViewDialog';
import WorkflowAssigneeDropdown from './WorkflowAssigneeDropdown';
import WorkflowTimelineCard from './WorkflowTimelineCard';
import TaskCreationDialog from '../tasks/TaskCreationDialog';
import TaskViewDialog from '../tasks/TaskViewDialog';
import WorkItemTimer from '../common/WorkItemTimer';
import TaskMappingDialog from '../tasks/TaskMappingDialog';
import { addNotification } from '../../store/slices/uiSlice';
import { createTask, fetchTaskById } from '../../store/slices/taskSlice';
import { messageHasTaskContent, descriptionHtmlFromMessage } from '../../utils/chatMappingUtils';
import { saveMessage, unsaveMessage, checkIfMessageSaved } from '../../store/slices/savedMessageSlice';
import { BookmarkIcon } from './SidebarIcons';
import { createVaultItem } from '../../features/vault/services/vaultService';
import SaveToVaultModal from '../../features/vault/components/SaveToVaultModal';
import { acknowledgeTemplateMessage } from '../../services/messageTemplateService';
import { upsertRealmDocs } from '../../realm';
import { formatEstimateTime } from '../../utils/estimateTime';
import { isWorkflowShellTask, isUnmappedTask } from '../../utils/workflowTaskUtils';

import PresignedAvatar from '../common/PresignedAvatar';
import MessageMentionContent from '../common/MessageMentionContent';
import { SensitiveUnlockModal, sensitiveAuthLines } from './SensitiveMessageModals';
import { downloadAttachment, openAttachmentFallback, extractUrlFromText, normalizeMessageContentHtml, unwrapEscapedHtmlContent, isRichTextMessageContent, isInternalAppTransportUrl, isDisplayableImage } from '../../utils/fileUtils';
import { renderHighlightedSearchText } from '../../utils/chatSearchUtils';
import 'react-quill/dist/quill.snow.css';

const MessageAttachmentsDisplay = ({
  attachments,
  attachmentGroupName,
  onViewImage,
  onDownload,
  onViewAll,
  onOpenGroup,
}) => {
  return (
    <AttachmentGroupBar
      attachments={attachments}
      groupName={attachmentGroupName || ''}
      expanded={false}
      onViewImage={onViewImage}
      onDownload={onDownload}
      onViewAll={onViewAll}
      onOpenGroup={onOpenGroup}
      mode="display"
    />
  );
};

const vaultContentsFromAttachments = (attachments) =>
  (Array.isArray(attachments) ? attachments : [])
    .flatMap((att) => (Array.isArray(att?.vaultContents) ? att.vaultContents : []))
    .map((line) => String(line || '').trim())
    .filter(Boolean);

const VaultZipContentsNote = ({ attachments, onLight }) => {
  const lines = vaultContentsFromAttachments(attachments);
  if (!lines.length) return null;
  return (
    <div
      className={`mt-2 select-none text-[12px] leading-snug ${
        onLight ? 'text-gray-600 dark:text-gray-300' : 'text-white/85'
      }`}
    >
      <div className={`font-semibold ${onLight ? 'text-gray-800 dark:text-gray-100' : 'text-white'}`}>
        Documents and Chat attached
      </div>
      {lines.map((line, index) => (
        <div key={`${line}-${index}`}>{line}</div>
      ))}
    </div>
  );
};

const getRecursiveThreadReplies = (messageId, allMsgs) => {
  const replies = [];
  const checkedIds = new Set([messageId]);
  let addedNew;
  do {
    addedNew = false;
    for (const m of allMsgs) {
      if (m.replyToMessageId && checkedIds.has(m.replyToMessageId) && !checkedIds.has(m.id)) {
        checkedIds.add(m.id);
        replies.push(m);
        addedNew = true;
      }
    }
  } while (addedNew);
  return replies.sort((a, b) => new Date(a.createdAt || a.timestamp) - new Date(b.createdAt || b.timestamp));
};

const getReplyPlainText = (msg) => {
  const raw = String(msg?.content || '').trim();
  if (!raw || raw === '<p><br></p>' || raw === '<p></p>') return '';
  return raw
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/** Short text-only replies can render under the bubble; otherwise use the thread summary. */
const INLINE_REPLY_MAX_COUNT = 2;
const INLINE_REPLY_MAX_CHARS = 50;

const isShareOnlyReply = (reply) => Boolean(resolveWorkItemShare(reply) || resolveChatThreadShare(reply));

const canShowInlineThreadReplies = (replies) => {
  if (!Array.isArray(replies) || replies.length === 0 || replies.length > INLINE_REPLY_MAX_COUNT) {
    return false;
  }
  return replies.every((reply) => {
    if (Array.isArray(reply.attachments) && reply.attachments.length > 0) return false;
    if (reply.meetingInfo?.joinUrl) return false;
    if (isShareOnlyReply(reply)) return true;
    const text = getReplyPlainText(reply);
    if (!text || text.length >= INLINE_REPLY_MAX_CHARS) return false;
    if (reply.linkPreview?.url) return false;
    return true;
  });
};

const resolveMessageKey = (msg, index = 0) => msg?.id || msg?.tempId || `idx-${index}`;

const messageToWorkflowItem = (msg, fallbackChannelId) => {
  if (!msg) return null;
  const messageId = msg.id || msg.messageId;
  const channelId = msg.channelId || fallbackChannelId;
  if (!messageId || !channelId) return null;
  return {
    messageId,
    channelId,
    content: msg.content,
    contentHtml: msg.content,
    workflowTitle: msg.workflowTitle,
    workflowDescription: msg.workflowDescription,
    workflowTrackId: msg.workflowTrackId,
    workflowTrackLink: msg.workflowTrackLink,
    workflowAssignedToUserId: msg.workflowAssignedToUserId,
    workflowAssignedToName: msg.workflowAssignedToName,
    workflowByCategory: getActiveWorkflowByCategory(
      mergeReactionsWithWorkflowCategory(msg.reactions || [], msg.workflowByCategory)
    ),
    customTags: msg.customTags || [],
    senderId: msg.senderId,
    senderName: msg.senderName,
    senderProfilePicture: msg.senderProfilePicture,
    createdAt: msg.createdAt || msg.timestamp,
    attachments: msg.attachments || [],
    attachmentGroupName: msg.attachmentGroupName || '',
    reactions: msg.reactions || [],
  };
};

const isWorkflowChatBubble = (msg) => messageIsRaisedWorkflow(msg?.reactions);

const ChevronRightIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
  </svg>
);

const TaskDocIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const DeliveryTickIcon = ({ state, onLight }) => {
  if (state === 'double' || state === 'read') {
    const colorClass = state === 'read'
      ? (onLight ? 'text-sky-500' : 'text-sky-300')
      : (onLight ? 'text-gray-400' : 'text-white/55');
    return (
      <svg
        className={`inline-block ml-0.5 align-[-1.5px] ${colorClass}`}
        width="16"
        height="11"
        viewBox="0 0 16 11"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M1.2 6.15L3.85 8.7L9.55 1.7"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6.05 6.15L8.7 8.7L14.4 1.7"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg
      className="inline-block ml-0.5 align-[-1.5px]"
      width="12"
      height="11"
      viewBox="0 0 12 11"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1.2 6.15L3.85 8.7L10.4 1.7"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const getWorkflowVisualForMessage = (msg) => {
  if (msg?.status === 'failed' || isLocalOnlyMessage(msg)) return null;
  return getMessageWorkflowVisual(msg.reactions || [], msg.workflowByCategory);
};

const getMessageUrgentEstimateMinutes = (msg, tasks) => {
  const fromTemplate = Number(msg?.templateInfo?.estimateTimeInMinutes);
  if (Number.isFinite(fromTemplate) && fromTemplate > 0) return fromTemplate;
  const messageId = msg?.id || msg?._id;
  if (!messageId || !Array.isArray(tasks) || !tasks.length) return undefined;
  const mins = tasks
    .filter((task) => String(task?.messageId || '') === String(messageId))
    .map((task) => Number(task?.estimateTimeInMinutes))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (!mins.length) return undefined;
  return Math.max(...mins);
};

const messageHasVisibleTextContent = (msg) => {
  if (msg?.sensitive) return true;
  const raw = String(msg?.content || '').trim();
  if (!raw || raw === '<p><br></p>' || raw === '<p></p>') return false;
  return raw.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim().length > 0;
};

/** Image-only bubble: one photo, no caption / meeting / link card. */
const isSingleImageOnlyBubble = (msg) => {
  if (!msg) return false;
  if (msg.meetingInfo?.joinUrl) return false;
  if (msg.appointmentInfo?.appointmentId) return false;
  if (msg.linkPreview) return false;
  if (messageHasVisibleTextContent(msg)) return false;
  const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];
  if (attachments.length !== 1) return false;
  return isDisplayableImage(attachments[0]);
};

const getMessageSectionClasses = (msg, isOwn, { blinkUrgent = false } = {}) => {
  if (msg?.status === 'failed') {
    if (isOwn) {
      return 'bg-red-500 text-white opacity-90';
    }
    return 'bg-red-100 dark:bg-red-900/40 text-gray-900 dark:text-white border border-red-200 dark:border-red-800 opacity-95';
  }
  const workflowVisual = getWorkflowVisualForMessage(msg);
  if (workflowVisual) {
    if (workflowVisual.isClosed) {
      return 'bg-green-50 dark:bg-green-950/40 text-gray-900 dark:text-gray-100 border border-green-200 dark:border-green-800/60';
    }
    if (workflowVisual.isUrgent) {
      return `bg-red-100 dark:bg-red-950/45 text-gray-900 dark:text-gray-100 border border-red-300 dark:border-red-800/70${
        blinkUrgent ? ' wl-urgent-blink' : ''
      }`;
    }
    return 'bg-yellow-50 dark:bg-yellow-950/35 text-gray-900 dark:text-gray-100 border border-yellow-200 dark:border-yellow-800/50';
  }
  if (isSingleImageOnlyBubble(msg)) {
    return 'bg-transparent shadow-none text-gray-900 dark:text-gray-100';
  }
  return 'bg-gray-100 dark:bg-gray-700/80 text-gray-900 dark:text-gray-100';
};

/** Bubble shell so urgent blink can use hooks (estimate-gated). */
const MessageSectionShell = ({
  msg,
  isOwn,
  estimateMinutes,
  isActiveCallMessage,
  hasEvent = false,
  transparentBubble = false,
  isActiveSearchMatch,
  isAssignDropdownOpenForMessage,
  chatMappingActive,
  sectionRoundingClass,
  onClick,
  role,
  title,
  messageRef,
  onMouseEnter,
  onMouseLeave,
  children,
}) => {
  const workflowVisual = getWorkflowVisualForMessage(msg);
  const urgentOpen = Boolean(
    workflowVisual?.isUrgent
    && !workflowVisual?.isClosed
    && msg?.urgentDeadlineAt
  );
  const blinkUrgent = useUrgentBlink(urgentOpen ? msg.urgentDeadlineAt : null, estimateMinutes);
  return (
    <div
      className={`relative overflow-visible w-full ${sectionRoundingClass} ${
        isActiveCallMessage
          ? 'text-gray-900 dark:text-white border border-indigo-300/80 dark:border-indigo-600/50 live-call-bubble-active shadow-sm'
          : transparentBubble || hasEvent
            ? 'bg-transparent shadow-none border-0'
            : getMessageSectionClasses(msg, isOwn, { blinkUrgent: urgentOpen && blinkUrgent })
      } ${isActiveSearchMatch ? 'ring-2 ring-yellow-400/90 ring-offset-2 ring-offset-transparent' : ''} ${
        isAssignDropdownOpenForMessage ? 'z-[150]' : ''
      } ${chatMappingActive ? 'cursor-pointer ring-2 ring-yellow-300/50 hover:ring-yellow-400/80 dark:ring-yellow-500/40' : ''}`}
      onClick={onClick}
      role={role}
      title={title}
      ref={messageRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </div>
  );
};

const isMessageTextOnLight = (msg, isOwn) => {
  if (msg?.status === 'failed') {
    return !isOwn;
  }
  return true;
};

const MessageItem = ({ messages, message, isOwn, isCallGroup = false, isLastInTimeline = false, isDirectMessage, currentUserId, onAddReaction, onEditMessage, onDeleteMessage, onReply, onViewThread, onPreviewThread, onCancelThreadHoverOpen, onForward, allMessages = [], onScrollToMessage, messageRefs, onRetryMessage, onMeetingMessageUpdated, onEditEvent, onCloneEvent, cloningEventId, expandCallNotesMeetingId, onJoinDirectCall, reminderPanelOpen = false, searchHighlightQuery = '', activeSearchMatchMessageId = null, assignableUsers = [], chatMappingActive = false, onMapMessage, channelId: channelIdProp }) => {
  const dispatch = useDispatch();
  const { savedMessageIds } = useSelector((state) => state.savedMessages);
  const allTasks = useSelector((state) => state.tasks?.tasks || []);
  const chatMessagesByChannel = useSelector((state) => state.chat?.messages || {});
  const allChatMessages = useMemo(
    () => Object.values(chatMessagesByChannel || {}).flatMap((list) => (Array.isArray(list) ? list : [])),
    [chatMessagesByChannel],
  );
  const users = useSelector((state) => state.users?.users || []);
  const preferredLanguage = useSelector((state) => state.auth?.user?.preferredLanguage || 'en');
  const channelSpace = useSelector((state) => {
    const id = channelIdProp || message?.channelId;
    if (!id) return { spaceId: '', spaceName: '' };
    const list = [...(state.channels?.channels || []), ...(state.channels?.directMessages || [])];
    const found = list.find((item) => item?.id === id);
    return { spaceId: found?.spaceId || '', spaceName: found?.spaceName || '' };
  });
  // Support both new grouped format (messages array) and old single message format
  const messageList = messages && Array.isArray(messages) ? messages : [message];
  const firstMessage = messageList[0];
  const lastMessage = messageList[messageList.length - 1];
  const isGrouped = messageList.length > 1;
  const isAttendanceGroup = messageList.some((m) => m.attendanceInfo?.attendanceEventId);
  const isNotificationGroup = messageList.some((m) => m.notificationInfo);
  const isCenteredActivityGroup = isAttendanceGroup || isNotificationGroup;
  const isWorkItemCardGroup = messageList.some((m) => isStandaloneTimelineCard(m) && !m.notificationInfo);
  
  // Use first message for sender info, last message for timestamp
  const displayMessage = firstMessage;

  const resolveSenderLabel = (msg) => {
    if (!msg) return 'Unknown User';
    if (msg.senderName && String(msg.senderName).trim()) return msg.senderName;
    const user = (users || []).find((u) => String(u.id) === String(msg.senderId))
      || (assignableUsers || []).find((u) => String(u.id) === String(msg.senderId));
    if (!user) return 'Unknown User';
    const first = (user.firstName || '').trim();
    const last = (user.lastName || '').trim();
    if (first && last) return `${first} ${last}`;
    if (first || last) return first || last;
    return user.username || user.label || 'Unknown User';
  };
  const resolveSenderAvatar = (msg) => {
    if (msg?.senderProfilePicture) return msg.senderProfilePicture;
    const user = (users || []).find((u) => String(u.id) === String(msg?.senderId));
    return user?.profilePicture || null;
  };
  const displaySenderName = resolveSenderLabel(displayMessage);
  const displaySenderAvatar = resolveSenderAvatar(displayMessage);
  
  const [showReactions, setShowReactions] = useState(null); // Track which message's reaction picker is open
  const [showActions, setShowActions] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState(null); // Track which message in the group is hovered (for action buttons)
  const [viewingGallery, setViewingGallery] = useState(null);
  const [viewingGroup, setViewingGroup] = useState(null);
  const [viewingAllAttachments, setViewingAllAttachments] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [profileUserId, setProfileUserId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sensitiveUnlockMessage, setSensitiveUnlockMessage] = useState(null);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteInitialDescription, setNoteInitialDescription] = useState('');
  const [noteMessageId, setNoteMessageId] = useState(null);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showMapTaskDialog, setShowMapTaskDialog] = useState(false);
  const [taskInitialDescription, setTaskInitialDescription] = useState('');
  const [taskMessageId, setTaskMessageId] = useState(null);
  const [taskMapTaskId, setTaskMapTaskId] = useState(null);
  const [showTrackDialog, setShowTrackDialog] = useState(false);
  const [trackDialogMessage, setTrackDialogMessage] = useState(null);
  const [viewingWorkflow, setViewingWorkflow] = useState(null);
  const [viewingLinkedTask, setViewingLinkedTask] = useState(null);
  const [tagInputMessageKey, setTagInputMessageKey] = useState(null);
  const [tagInputDraft, setTagInputDraft] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [vaultSaveMessage, setVaultSaveMessage] = useState(null);
  const [vaultSaving, setVaultSaving] = useState(false);
  const [assignPickerMessageKey, setAssignPickerMessageKey] = useState(null);
  const [assignDropdownOpenMessageKey, setAssignDropdownOpenMessageKey] = useState(null);
  const [assignSearchQuery, setAssignSearchQuery] = useState('');
  const [reactionPickerPortalPosition, setReactionPickerPortalPosition] = useState(null); // { top, left } for portal on small screens
  const [menuPortalPosition, setMenuPortalPosition] = useState(null); // { top, left } for portal placement of the dropdown menu
  const [expandedMessages, setExpandedMessages] = useState({}); // Track expanded state for long messages
  const menuButtonRefs = useRef({});
  const menuPortalRef = useRef(null);
  const menuPreferAboveRef = useRef(false);
  const reactionPickerRefs = useRef({}); // Track refs for each message's reaction picker
  const pendingReactionClicks = useRef(new Set());
  const tagInputRef = useRef(null);
  const assignPickerRef = useRef(null);
  const editQuillRef = useRef(null);

  const PICKER_MAX_WIDTH = 360;
  const PICKER_ESTIMATED_HEIGHT = 320;
  const PICKER_GAP = 8;
  const REACTION_PICKER_Z_INDEX = 99999;
  const reactionPickerPortalRef = useRef(null);

  const computeReactionPickerPortalPosition = (anchorEl, pickerHeight = PICKER_ESTIMATED_HEIGHT) => {
    const rect = anchorEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const height = Math.max(120, pickerHeight || PICKER_ESTIMATED_HEIGHT);
    let left = rect.left;
    if (left + PICKER_MAX_WIDTH > vw) left = Math.max(0, vw - PICKER_MAX_WIDTH);
    if (left < 0) left = 0;

    const spaceBelow = vh - rect.bottom - PICKER_GAP;
    const spaceAbove = rect.top - PICKER_GAP;
    const openAbove = spaceBelow < height && spaceAbove > spaceBelow;

    let top = openAbove
      ? rect.top - height - PICKER_GAP
      : rect.bottom + PICKER_GAP;

    if (top + height > vh - PICKER_GAP) {
      top = Math.max(PICKER_GAP, vh - height - PICKER_GAP);
    }
    if (top < PICKER_GAP) top = PICKER_GAP;
    return { top, left };
  };

  const updateReactionPickerPortalPosition = (messageKey) => {
    const anchorEl = reactionPickerRefs.current[messageKey] || menuButtonRefs.current[messageKey];
    if (!anchorEl) return;
    const pickerHeight = reactionPickerPortalRef.current?.offsetHeight || PICKER_ESTIMATED_HEIGHT;
    setReactionPickerPortalPosition(computeReactionPickerPortalPosition(anchorEl, pickerHeight));
  };

  const openReactionPicker = (messageKey) => {
    // Cancel pending hover-thread open so the panel doesn't pop open under the picker.
    onCancelThreadHoverOpen?.();
    setShowReactions((current) => (current === messageKey ? null : messageKey));
  };

  const MENU_MAX_WIDTH = 192; // w-48 is 12rem (192px)
  const MENU_ESTIMATED_HEIGHT = 400;
  const MENU_GAP = 4;

  const computeMenuPortalPosition = (anchorEl, menuHeight = MENU_ESTIMATED_HEIGHT, preferAbove = false) => {
    const rect = anchorEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = rect.left;
    if (rect.right > vw / 2 || rect.left + MENU_MAX_WIDTH > vw) {
      left = rect.right - MENU_MAX_WIDTH;
    }
    if (left < 0) left = 0;
    if (left + MENU_MAX_WIDTH > vw) left = vw - MENU_MAX_WIDTH;

    const spaceBelow = vh - rect.bottom - MENU_GAP;
    const spaceAbove = rect.top - MENU_GAP;
    const shouldOpenAbove = preferAbove
      || (spaceBelow < menuHeight && spaceAbove > spaceBelow)
      || (spaceBelow < menuHeight && spaceAbove >= menuHeight * 0.5);

    let top = shouldOpenAbove
      ? rect.top - menuHeight - MENU_GAP
      : rect.bottom + MENU_GAP;

    if (top + menuHeight > vh - MENU_GAP) {
      top = vh - menuHeight - MENU_GAP;
    }
    if (top < MENU_GAP) {
      top = MENU_GAP;
    }

    return { top, left };
  };

  const updateMenuPortalPosition = (menuId) => {
    const anchorEl = menuButtonRefs.current[menuId];
    if (!anchorEl) return;
    const menuHeight = menuPortalRef.current?.offsetHeight || MENU_ESTIMATED_HEIGHT;
    setMenuPortalPosition(
      computeMenuPortalPosition(anchorEl, menuHeight, menuPreferAboveRef.current),
    );
  };

  const toggleMessageMenu = (messageKey, preferAbove = false) => {
    if (openMenuId === messageKey) {
      setOpenMenuId(null);
      return;
    }
    onCancelThreadHoverOpen?.();
    menuPreferAboveRef.current = preferAbove;
    setOpenMenuId(messageKey);
  };

  // Compute portal position for reaction picker so it stays in viewport and above all UI layers
  useLayoutEffect(() => {
    if (showReactions == null) {
      setReactionPickerPortalPosition(null);
      return undefined;
    }
    updateReactionPickerPortalPosition(showReactions);
    const rafId = window.requestAnimationFrame(() => {
      updateReactionPickerPortalPosition(showReactions);
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [showReactions]);

  // Keep reaction picker under the smiley while scrolling/resizing. Do not close on scroll —
  // opening the reply thread used to fire scroll and hide the picker ~50–100px off-target.
  useEffect(() => {
    if (showReactions == null) return undefined;

    let rafId = 0;
    let trackRafId = 0;
    let lastTop = null;
    let lastLeft = null;

    const update = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        updateReactionPickerPortalPosition(showReactions);
      });
    };

    const trackAnchor = () => {
      const anchorEl = reactionPickerRefs.current[showReactions] || menuButtonRefs.current[showReactions];
      if (anchorEl) {
        const rect = anchorEl.getBoundingClientRect();
        if (lastTop !== rect.top || lastLeft !== rect.left) {
          lastTop = rect.top;
          lastLeft = rect.left;
          updateReactionPickerPortalPosition(showReactions);
        }
      }
      trackRafId = window.requestAnimationFrame(trackAnchor);
    };

    document.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    trackRafId = window.requestAnimationFrame(trackAnchor);
    return () => {
      document.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      if (rafId) window.cancelAnimationFrame(rafId);
      if (trackRafId) window.cancelAnimationFrame(trackRafId);
    };
  }, [showReactions]);

  useEffect(() => {
    if (tagInputMessageKey && tagInputRef.current) {
      tagInputRef.current.focus();
    }
  }, [tagInputMessageKey]);

  useEffect(() => {
    if (!assignPickerMessageKey) return undefined;

    const handleClickOutside = (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-workflow-assign-dropdown-panel]')) {
        return;
      }
      if (assignPickerRef.current && !assignPickerRef.current.contains(target)) {
        setAssignPickerMessageKey(null);
        setAssignDropdownOpenMessageKey(null);
        setAssignSearchQuery('');
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        if (assignDropdownOpenMessageKey === assignPickerMessageKey) {
          setAssignDropdownOpenMessageKey(null);
          setAssignSearchQuery('');
          return;
        }
        setAssignPickerMessageKey(null);
        setAssignDropdownOpenMessageKey(null);
        setAssignSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [assignPickerMessageKey, assignDropdownOpenMessageKey]);

  // Compute portal position for three-dot dropdown menu so it stays in viewport and doesn't get clipped
  useLayoutEffect(() => {
    if (openMenuId == null) {
      setMenuPortalPosition(null);
      return undefined;
    }
    updateMenuPortalPosition(openMenuId);
    const rafId = window.requestAnimationFrame(() => updateMenuPortalPosition(openMenuId));
    return () => window.cancelAnimationFrame(rafId);
  }, [openMenuId]);

  // Keep portal menu positioned while scrolling/resizing
  useEffect(() => {
    if (openMenuId == null) return;

    let rafId = 0;
    const update = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        updateMenuPortalPosition(openMenuId);
      });
    };

    document.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      document.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [openMenuId]);

  // Rich Text Editor Configuration for editing
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link'
  ];

  // Close reaction picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const eventTarget = event.target;
      const targetEl = eventTarget instanceof Element
        ? eventTarget
        : eventTarget?.parentElement;

      const isClickInsideInlinePicker = Object.values(reactionPickerRefs.current).some(
        (ref) => ref && ref.contains(eventTarget),
      );
      const isClickInsidePortalPicker = targetEl?.closest?.('[data-reaction-picker]');

      if (!isClickInsideInlinePicker && !isClickInsidePortalPicker && showReactions !== null) {
        setShowReactions(null);
      }
    };

    if (showReactions !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showReactions]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideMenuBtn = event.target.closest('.message-menu-container');
      const isClickInsidePortalMenu = (event.target instanceof Element)
        ? event.target.closest('[data-message-menu]')
        : null;
      if (!isClickInsideMenuBtn && !isClickInsidePortalMenu && openMenuId !== null) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openMenuId]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Calculate difference in days
    const diffTime = today - messageDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // Format date as dd-MMM-yyyy
    const formatDate = (dateObj) => {
      const day = String(dateObj.getDate()).padStart(2, '0');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[dateObj.getMonth()];
      const year = dateObj.getFullYear();
      return `${day}-${month}-${year}`;
    };
    
    // Format time as HH:mm
    const formatTimeOnly = (dateObj) => {
      return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };
    
    if (diffDays === 0) {
      // Today - show time only
      return formatTimeOnly(date);
    } else if (diffDays === 1) {
      // Yesterday - show "Yesterday"
      return `Yesterday ${formatTimeOnly(date)}`;
    } else {
      // More than 1 day ago - show date in dd-MMM-yyyy format with time
      return `${formatDate(date)} ${formatTimeOnly(date)}`;
    }
  };

  const formatBubbleTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Strip HTML tags from content for preview, preserving newlines
  const stripHtmlTags = (html) => {
    if (!html) return '';
    // Create a temporary div element to parse HTML
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    
    // Convert <br> and <br/> to newlines before getting text
    const brRegex = /<br\s*\/?>/gi;
    tmp.innerHTML = tmp.innerHTML.replace(brRegex, '\n');
    
    // Convert <p> tags to newlines (with newline before closing tag)
    const pRegex = /<\/p>/gi;
    tmp.innerHTML = tmp.innerHTML.replace(pRegex, '\n</p>');
    
    // Get text content - this will preserve the newlines we added
    let text = tmp.textContent || tmp.innerText || '';
    
    // Clean up: replace multiple spaces with single space, but preserve newlines
    text = text.replace(/[ \t]+/g, ' ');
    // Replace multiple consecutive newlines with double newline (paragraph break)
    text = text.replace(/\n{3,}/g, '\n\n');
    // Trim but preserve intentional line breaks
    return text.trim();
  };

  // Handle task creation dialog opening
  const handleOpenTaskDialog = (message, mapTaskId = null) => {
    if (!message || !message.id) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Invalid message for task creation',
        })
      );
      return;
    }

    const htmlContent = descriptionHtmlFromMessage(message);

    if (!messageHasTaskContent(message)) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Message content is empty. Cannot create task.',
        })
      );
      return;
    }

    // Set initial description with HTML content and message ID for the dialog
    setTaskInitialDescription(htmlContent);
    setTaskMessageId(message.id);
    setTaskMapTaskId(mapTaskId);
    setShowTaskDialog(true);
  };

  const findLinkedTaskForMessage = (message) => {
    const messageId = message?.id || message?.messageId;
    if (!messageId) return null;
    const matches = (allTasks || []).filter((task) => String(task?.messageId || '') === String(messageId));
    if (!matches.length) return null;

    // Prefer a real titled task on this message (not an empty workflow shell).
    const titledUnmapped = matches.find((task) => (
      isUnmappedTask(task)
      && (task.title || '').trim()
      && !isWorkflowShellTask(task)
    ));
    if (titledUnmapped) return titledUnmapped;

    // Mapped chat row → open the parent task in the task dialog.
    const mappedChild = matches.find((task) => task?.mapTaskId && String(task.mapTaskId).trim());
    if (mappedChild?.mapTaskId) {
      const parent = (allTasks || []).find((task) => String(task?.id || '') === String(mappedChild.mapTaskId));
      if (parent) return parent;
      return mappedChild;
    }

    return matches.find((task) => isUnmappedTask(task)) || matches[0];
  };

  const lookupAssignableUser = (userId) => {
    if (!userId) return null;
    return (assignableUsers || []).find((item) => String(item.id) === String(userId))
      || (users || []).find((item) => String(item.id) === String(userId))
      || null;
  };

  const lookupAssignableName = (userId) => {
    if (!userId) return '';
    const person = lookupAssignableUser(userId);
    if (!person) return '';
    if (person.label) return person.label;
    const name = [person.firstName, person.lastName].filter(Boolean).join(' ').trim()
      || person.username
      || person.email
      || '';
    if (currentUserId && String(userId) === String(currentUserId)) {
      return name ? `You (${name})` : 'You';
    }
    return name;
  };

  const lookupAssignablePicture = (userId) => {
    const person = lookupAssignableUser(userId);
    return person?.profilePicture || '';
  };

  const resolveMessageAssignee = (msg, linkedTask) => {
    const fromMessage = msg?.workflowAssignedToUserId;
    if (fromMessage) {
      const name = (currentUserId && String(fromMessage) === String(currentUserId))
        ? (msg.workflowAssignedToName
          ? `You (${msg.workflowAssignedToName})`
          : lookupAssignableName(fromMessage))
        : (msg.workflowAssignedToName || lookupAssignableName(fromMessage));
      return { id: fromMessage, name, profilePicture: lookupAssignablePicture(fromMessage) };
    }
    if (!linkedTask) return { id: null, name: '', profilePicture: '' };
    const delegates = Array.isArray(linkedTask.delegateId)
      ? linkedTask.delegateId.filter(Boolean)
      : (linkedTask.delegateId ? [linkedTask.delegateId] : []);
    const assignedId = delegates[0] || linkedTask.createdByUserId || null;
    if (!assignedId) return { id: null, name: '', profilePicture: '' };
    return { id: assignedId, name: lookupAssignableName(assignedId), profilePicture: lookupAssignablePicture(assignedId) };
  };

  const isWorkTimelineCardMessage = (message) => {
    if (!message || message.status === 'failed') return false;
    if (isWorkflowChatBubble(message)) return true;
    const mergedReactions = mergeReactionsWithWorkflowCategory(
      message.reactions || [],
      message.workflowByCategory
    );
    if (messageIsTaskWorkItem(mergedReactions) || messageIsTaskWorkItem(message.reactions)) return true;
    const visual = getWorkflowVisualForMessage(message);
    if (visual && String(visual.watermarkLabel || '').toLowerCase() === 'task') return true;
    const linked = findLinkedTaskForMessage(message);
    return Boolean(linked && String(linked.title || '').trim() && !isWorkflowShellTask(linked));
  };

  const handleOpenWorkItemPopup = async (message) => {
    const linked = findLinkedTaskForMessage(message);
    const namedTask = linked
      && (linked.title || '').trim()
      && !isWorkflowShellTask(linked)
      ? linked
      : null;
    const isWorkflow = isWorkflowChatBubble(message);

    // Real tasks (mapped chats / +tasks) open the task dialog — not the workflow dialog.
    if (namedTask && !isWorkflow) {
      try {
        const task = await dispatch(fetchTaskById(namedTask.id)).unwrap();
        setViewingWorkflow(null);
        setViewingLinkedTask(task || namedTask);
      } catch {
        setViewingWorkflow(null);
        setViewingLinkedTask(namedTask);
      }
      return;
    }

    if (isWorkflow) {
      const item = messageToWorkflowItem(message, channelIdProp);
      if (item) {
        setViewingLinkedTask(null);
        setViewingWorkflow(item);
        return;
      }
    }

    if (namedTask?.id) {
      try {
        const task = await dispatch(fetchTaskById(namedTask.id)).unwrap();
        setViewingWorkflow(null);
        setViewingLinkedTask(task || namedTask);
      } catch {
        setViewingWorkflow(null);
        setViewingLinkedTask(namedTask);
      }
    }
  };

  // Handle map task dialog opening
  const handleOpenMapTaskDialog = (message) => {
    // Use the same validation logic as handleOpenTaskDialog
    if (!message || !message.id) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Invalid message for task mapping',
        })
      );
      return;
    }

    const htmlContent = descriptionHtmlFromMessage(message);

    if (!messageHasTaskContent(message)) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Message content is empty. Cannot create task.',
        })
      );
      return;
    }

    // Store message data for later use (same as create task)
    setTaskInitialDescription(htmlContent);
    setTaskMessageId(message.id);
    setShowMapTaskDialog(true);
  };

  // Handle task selected from mapping dialog
  const handleTaskSelected = async (selectedTask) => {
    // Close mapping dialog first
    setShowMapTaskDialog(false);

    // Validate messageId
    if (!taskMessageId) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Message ID is missing. Cannot create task.',
        })
      );
      return;
    }

    // Description is seeded from the chat (text, or "sent image" / file for attachments)
    if (!taskInitialDescription) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Message content is empty. Cannot create task.',
        })
      );
      return;
    }

    // Directly create the mapped task without showing the dialog
    try {
      const taskData = {
        title: '', // No title for mapped tasks
        description: taskInitialDescription,
        messageId: taskMessageId,
        mapTaskId: selectedTask.id,
        // Don't send status when mapping to existing task - backend will handle it
      };

      await dispatch(createTask(taskData)).unwrap();

      dispatch(
        addNotification({
          type: 'success',
          message: 'Message mapped to task successfully!',
        })
      );

      window.dispatchEvent(new CustomEvent('workloop:stick-timeline-to-latest', {
        detail: { messageId: taskMessageId },
      }));

      // Clear the stored data
      setTaskInitialDescription('');
      setTaskMessageId(null);
      setTaskMapTaskId(null);
    } catch (error) {
      console.error('Failed to create mapped task:', error);
      dispatch(
        addNotification({
          type: 'error',
          message: error || 'Failed to map task. Please try again.',
        })
      );
    }
  };

  // Handle save/unsave message
  const handleSaveMessage = async (messageId) => {
    try {
      const isSaved = savedMessageIds.includes(messageId);
      if (isSaved) {
        await dispatch(unsaveMessage(messageId)).unwrap();
        dispatch(
          addNotification({
            type: 'success',
            message: 'Bookmark removed',
          })
        );
      } else {
        await dispatch(saveMessage(messageId)).unwrap();
        dispatch(
          addNotification({
            type: 'success',
            message: 'Bookmarked',
          })
        );
      }
      setOpenMenuId(null);
    } catch (error) {
      console.error('Failed to update bookmark:', error);
      const errorMessage = typeof error === 'string'
        ? error
        : error?.message || error?.toString() || 'Failed to update bookmark. Please try again.';
      dispatch(
        addNotification({
          type: 'error',
          message: errorMessage,
        })
      );
    }
  };

  // Check if message is saved
  const isMessageSaved = (messageId) => {
    return savedMessageIds.includes(messageId);
  };

  const vaultDefaultTitle = (msg) => {
    const text = stripHtmlTags(msg?.content || '').replace(/\s+/g, ' ').trim();
    if (text) return text.slice(0, 80);
    const fileName = (msg?.attachments || []).find((item) => item?.fileName)?.fileName;
    return fileName || 'Untitled';
  };

  const handleConfirmSaveToVault = async (title) => {
    const msg = vaultSaveMessage;
    if (!msg) return;
    setVaultSaving(true);
    const attachments = (msg.attachments || [])
      .filter((item) => item?.fileUrl && String(item.fileType || '').toUpperCase() !== 'LINK')
      .map((item) => ({
        id: item.id,
        fileName: item.fileName,
        fileUrl: item.fileUrl,
        fileType: item.fileType,
        fileSize: item.fileSize || 0,
      }));
    if (!stripHtmlTags(msg.content || '') && attachments.length === 0) {
      dispatch(addNotification({ type: 'error', message: 'This message has nothing to save in Vault' }));
      setVaultSaving(false);
      return;
    }
    try {
      await createVaultItem({
        title,
        content: msg.content || '',
        sourceMessageId: msg.id,
        sourceChannelId: msg.channelId,
        attachments,
      });
      dispatch(addNotification({ type: 'success', message: 'Saved to Vault' }));
      window.dispatchEvent(new CustomEvent('workloop:vault-updated'));
      setVaultSaveMessage(null);
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error?.response?.data?.error || error?.message || 'Could not save to Vault',
      }));
    } finally {
      setVaultSaving(false);
    }
  };

  const handleDownload = async (attachment) => {
    try {
      await downloadAttachment(attachment);
    } catch (error) {
      console.error('Download error:', error);
      openAttachmentFallback(attachment);
    }
  };

  const handleReactionClick = (messageId, reactionId, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();

    if (!messageId || !reactionId) return;

    const reactionKey = canonicalReactionId(reactionId);
    if (!reactionKey) return;

    // Work Item Type → Task creates a task only (never a workflow).
    if (reactionKey === WORK_ITEM_TYPE_TASK_ID) {
      const target = messageList.find((m) => String(m.id || m._id || m.tempId) === String(messageId));
      const existingType = getActiveWorkflowByCategory(target?.reactions || [])['work-item-type'];
      if (existingType) {
        dispatch(addNotification({
          type: 'error',
          message: existingType === WORK_ITEM_TYPE_TASK_ID
            ? 'This message already has a work item type'
            : 'Cannot change a workflow to a task',
        }));
        setShowReactions(null);
        return;
      }
      if (target) {
        handleOpenTaskDialog(target);
      }
      setShowReactions(null);
      return;
    }

    const pendingKey = `${messageId}:${reactionKey}`;
    if (pendingReactionClicks.current.has(pendingKey)) return;
    pendingReactionClicks.current.add(pendingKey);

    const result = onAddReaction(messageId, reactionKey);
    Promise.resolve(result).finally(() => {
      pendingReactionClicks.current.delete(pendingKey);
    });

    setShowReactions(null);
  };

  const handleOpenTrackDialog = (msg) => {
    setTrackDialogMessage(msg);
    setShowTrackDialog(true);
  };

  const handleSaveWorkflowTrack = async ({ trackId, trackLink, messageId, removed }) => {
    try {
      await dispatch(updateWorkflowTrack({
        messageId,
        trackId,
        trackLink,
        clear: Boolean(removed),
      })).unwrap();
      window.dispatchEvent(new CustomEvent('workloop:workflows-assigned-changed', {
        detail: { messageId },
      }));
      dispatch(addNotification({
        type: 'success',
        message: trackId ? 'Track ID saved' : 'Track ID removed',
      }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: typeof error === 'string' ? error : 'Failed to save track ID',
      }));
    }
  };

  const handleOpenCustomTagInput = (messageKey) => {
    setAssignPickerMessageKey(null);
    setTagInputMessageKey(messageKey);
    setTagInputDraft('');
  };

  const handleCloseCustomTagInput = () => {
    setTagInputMessageKey(null);
    setTagInputDraft('');
  };

  const handleOpenAssignPicker = (messageKey) => {
    setTagInputMessageKey(null);
    setTagInputDraft('');
    setAssignPickerMessageKey(messageKey);
    setAssignDropdownOpenMessageKey(messageKey);
    setAssignSearchQuery('');
  };

  const handleCloseAssignPicker = () => {
    setAssignPickerMessageKey(null);
    setAssignDropdownOpenMessageKey(null);
    setAssignSearchQuery('');
  };

  const handleSubmitCustomTag = async (messageId, messageKey) => {
    const label = tagInputDraft.trim();
    if (!label) {
      handleCloseCustomTagInput();
      return;
    }

    try {
      await dispatch(addMessageCustomTag({ messageId, label })).unwrap();
      setTagInputDraft('');
      if (tagInputMessageKey === messageKey) {
        setTimeout(() => tagInputRef.current?.focus(), 0);
      }
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: typeof error === 'string' ? error : 'Failed to add tag',
      }));
    }
  };

  const handleRemoveCustomTag = async (messageId, tagId) => {
    try {
      await dispatch(removeMessageCustomTag({ messageId, tagId })).unwrap();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: typeof error === 'string' ? error : 'Failed to remove tag',
      }));
    }
  };

  const handleMarkWorkflowComplete = (msg) => {
    const messageId = msg.id || msg.tempId;
    handleReactionClick(messageId, WORKFLOW_CLOSED_STATUS_ID);
    setOpenMenuId(null);
  };

  const handleAssignWorkflow = async (msg, userId) => {
    const messageId = msg.id || msg.tempId;
    try {
      await dispatch(assignWorkflowMessage({ messageId, userId })).unwrap();
      dispatch(addNotification({
        type: 'success',
        message: messageIsRaisedWorkflow(msg.reactions) ? 'Workflow item assigned' : 'Task assigned',
      }));
      window.dispatchEvent(new CustomEvent('workloop:workflows-assigned-changed', {
        detail: { messageId },
      }));
      setOpenMenuId(null);
      handleCloseAssignPicker();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: typeof error === 'string' ? error : 'Failed to assign workflow item',
      }));
    }
  };

  const handleEdit = (msg) => {
    setIsEditing(true);
    setEditingMessageId(msg.id);
    const content = msg.content || '';
    setEditContent(content);
    // Set content in Quill editor after a short delay to ensure it's mounted
    setTimeout(() => {
      const quillEditor = editQuillRef.current?.getEditor();
      if (quillEditor) {
        quillEditor.root.innerHTML = content || '<p><br></p>';
        // Focus the editor
        quillEditor.focus();
      }
    }, 150);
  };

  const handleSaveEdit = async () => {
    if (editingMessageId) {
      // Get content from Quill editor
      const quillEditor = editQuillRef.current?.getEditor();
      const content = quillEditor ? quillEditor.root.innerHTML : editContent;
      
      // Strip empty paragraphs and check if content is meaningful
      const strippedContent = content.replace(/<p><br><\/p>/g, '').trim();
      
      if (strippedContent) {
        const msgToEdit = messageList.find(m => m.id === editingMessageId);
        if (msgToEdit && content !== msgToEdit.content) {
          try {
            await onEditMessage(editingMessageId, content);
            setIsEditing(false);
            setEditingMessageId(null);
            setEditContent('');
          } catch (error) {
            console.error('Error editing message:', error);
            alert('Failed to edit message: ' + (error.response?.data?.error || error.message || 'Unknown error'));
          }
        } else {
          setIsEditing(false);
          setEditingMessageId(null);
          setEditContent('');
        }
      } else {
        setIsEditing(false);
        setEditingMessageId(null);
        setEditContent('');
      }
    } else {
      setIsEditing(false);
      setEditingMessageId(null);
      setEditContent('');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent('');
    setEditingMessageId(null);
  };

  const handleDelete = (messageId) => {
    setMessageToDelete(messageId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (messageToDelete) {
      try {
        await onDeleteMessage(messageToDelete);
      } catch (error) {
        console.error('Error deleting message:', error);
        alert('Failed to delete message: ' + (error.response?.data?.error || error.message || 'Unknown error'));
      }
    }
    setMessageToDelete(null);
  };

  const handleCopyMessage = async (msg) => {
    const text = stripHtmlTags(msg.content || '');
    const hasAttachments = msg.attachments && msg.attachments.length > 0;
    const attachmentNames = hasAttachments
      ? msg.attachments.map((a) => a.originalFileName || a.fileName || 'file').join(', ')
      : '';
    const toCopy = text
      ? (attachmentNames ? `${text}\n\nAttachments: ${attachmentNames}` : text)
      : (attachmentNames ? `Attachments: ${attachmentNames}` : '');
    if (!toCopy.trim()) {
      dispatch(addNotification({ type: 'info', message: 'Nothing to copy' }));
      return;
    }
    try {
      await copyTextToClipboard(toCopy);
      dispatch(addNotification({ type: 'success', message: 'Message copied to clipboard' }));
    } catch (err) {
      console.log('something went wrong while copying message', err);

      dispatch(addNotification({ type: 'error', message: 'Failed to copy message' }));
    }
  };

  const handleCopyThreadLink = async (msg) => {
    const messageId = msg?.id || msg?._id;
    if (!messageId || msg.status === 'pending' || msg.status === 'failed' || String(messageId).startsWith('pending')) {
      dispatch(addNotification({ type: 'info', message: 'Wait until this message is sent' }));
      return;
    }
    const channelId = msg.channelId || firstMessage?.channelId || channelIdProp;
    const isReply = Boolean(msg.replyToMessageId);
    const rootId = getTopLevelParentId(msg, allMessages) || msg.replyToMessageId || '';
    const replies = isReply ? [] : getRecursiveThreadReplies(messageId, allMessages);
    const threadId = isReply ? rootId : (replies.length > 0 ? messageId : '');
    const url = buildChatThreadShareUrl({ channelId, messageId, threadId });
    if (!url) {
      dispatch(addNotification({ type: 'error', message: 'Could not create a thread link' }));
      return;
    }
    try {
      await copyTextToClipboard(url);
      dispatch(addNotification({ type: 'success', message: 'Thread link copied' }));
    } catch (err) {
      dispatch(addNotification({ type: 'error', message: 'Failed to copy thread link' }));
    }
  };

  const handleCopyShareLink = async (url, successMessage = 'Link copied') => {
    if (!url) {
      dispatch(addNotification({ type: 'error', message: 'Could not create a link' }));
      return;
    }
    try {
      await copyTextToClipboard(url);
      dispatch(addNotification({ type: 'success', message: successMessage }));
    } catch (err) {
      dispatch(addNotification({ type: 'error', message: 'Failed to copy link' }));
    }
  };

  // For DMs: no tick = local only, single = on server, grey double = delivered, blue double = read
  const isInThread = !onViewThread;
  const isThreadReply = isInThread && !!firstMessage.replyToMessageId;

  const reactionTargetMessage = showReactions != null
    ? messageList.find((m, i) => resolveMessageKey(m, i) === showReactions) ?? null
    : null;

  const openUserProfile = (userId) => {
    if (userId) {
      setProfileUserId(userId);
    }
  };

  const hasActiveMeetingInGroup = messageList.some(
    (m) => m.meetingInfo?.joinUrl && isMeetingActive(m.meetingInfo),
  );
  const hasWorkflowTaskCard = messageList.some((m) => isWorkTimelineCardMessage(m));
  const callGroupRightAligned = isCallGroup && isOwn && !hasActiveMeetingInGroup && !isInThread;
  const normalMessageMaxWidthClass = 'max-w-[92%] md:max-w-[86%] lg:max-w-[76%] xl:max-w-[70%]';
  const messageGroupWidthClass = hasActiveMeetingInGroup
    ? 'w-full max-w-full'
    : hasWorkflowTaskCard
      ? 'w-[max(380px,45%)] min-w-[380px]'
      : normalMessageMaxWidthClass;

  return (
    <div 
      className={`flex w-full ${isCenteredActivityGroup ? 'justify-center' : hasActiveMeetingInGroup ? 'justify-start' : isOwn && !isInThread ? 'justify-end' : 'justify-start'} ${isThreadReply ? 'ml-6 md:ml-8 border-l-2 border-primary-200 dark:border-primary-900/50 pl-3 md:pl-4' : ''} ${isNotificationGroup ? '!mt-1.5' : ''} ${isWorkItemCardGroup ? '!my-[5px]' : ''} group overflow-visible`}
      onMouseEnter={() => !isCenteredActivityGroup && setShowActions(true)}
      onMouseLeave={() => !isCenteredActivityGroup && setShowActions(false)}
    >
      <div className={`${isCenteredActivityGroup ? 'w-full max-w-lg' : messageGroupWidthClass} overflow-visible ${isOwn && !isInThread && !hasActiveMeetingInGroup && !isCenteredActivityGroup ? 'order-2' : 'order-1'}`}>
        {(!isOwn || isInThread) && !isCenteredActivityGroup && (
          <div className="flex items-center space-x-2 mb-1">
            <button
              type="button"
              onClick={() => openUserProfile(displayMessage.senderId)}
              className="flex-shrink-0 rounded-full hover:opacity-90 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-opacity"
              title={`View ${displaySenderName || 'user'}'s profile`}
              aria-label={`View ${displaySenderName || 'user'}'s profile`}
            >
              <PresignedAvatar
                fileKey={displaySenderAvatar}
                name={displaySenderName}
                  className="w-6 h-6 rounded-full object-cover"
                fallbackClassName="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center text-white text-[10px] font-medium"
              />
            </button>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {isCallGroup
                ? (displayMessage.meetingInfo?.label || 'Call')
                : displaySenderName}
            </span>
          </div>
        )}
        
        <div className="relative flex items-start gap-1.5 overflow-visible min-w-0 max-w-full">
          {/* Message bubble — per-message workflow styling and reactions when grouped */}
          <div
            className={`${hasActiveMeetingInGroup || isNotificationGroup ? 'w-full' : hasWorkflowTaskCard ? 'w-full' : 'w-fit'} ${hasWorkflowTaskCard ? '' : 'min-w-0 max-w-full'} break-words overflow-visible ${isGrouped && !isNotificationGroup ? 'rounded-xl shadow-sm' : ''}`}
          >
              {messageList.map((msg, msgIndex) => {
                const messageKey = resolveMessageKey(msg, msgIndex);
                const reactionMessageId = msg.id || msg.tempId;
                const msgIsOwn = msg.senderId === currentUserId;
                const bubbleOwn = isCallGroup
                  ? (msg.meetingInfo?.joinUrl ? isOwn : msgIsOwn)
                  : isOwn;
                const msgWorkflowVisual = getWorkflowVisualForMessage(msg);
                const msgBubbleTextOnLight = isMessageTextOnLight(msg, bubbleOwn);
                const isFirstInGroup = msgIndex === 0;
                const isLastInGroup = msgIndex === messageList.length - 1;
                const hasMeeting = Boolean(msg.meetingInfo?.joinUrl);
                const isActiveCallMessage = Boolean(
                  msg.meetingInfo?.joinUrl && isMeetingActive(msg.meetingInfo),
                );
                const hasEvent = Boolean(msg.eventInfo?.eventId);
                const hasAppointment = Boolean(msg.appointmentInfo?.appointmentId);
                const share = withWorkItemShareTitle(resolveWorkItemShare(msg), {
                  tasks: allTasks,
                  messages: [...allMessages, ...allChatMessages],
                });
                const hasShare = Boolean(share);
                const threadShare = !hasShare ? resolveChatThreadShare(msg) : null;
                const hasThreadShare = Boolean(threadShare);
                const canShareEvent = !hasEvent || Boolean(msg.eventInfo?.allowInviteOthers);
                const hasContent = !hasEvent && !hasAppointment && !hasShare && !hasThreadShare && messageHasVisibleTextContent(msg);
                const hasReply = msg.replyToMessageId;
                const hasOnlyAttachments = !hasContent && !hasReply && !hasMeeting && !hasEvent && !hasAppointment && !hasShare && !hasThreadShare && msg.attachments && msg.attachments.length > 0;
                const isSingleImageOnly = isSingleImageOnlyBubble(msg);
                const sectionRoundingClass = isGrouped
                  ? `${isFirstInGroup ? 'rounded-t-xl' : ''} ${isLastInGroup ? 'rounded-b-xl' : ''}`.trim()
                  : (isSingleImageOnly ? 'rounded-xl' : 'rounded-xl shadow-sm');
                const isEditingThisMsg = isEditing && editingMessageId === msg.id;
                const isWithin30Minutes = msg.createdAt ? (new Date() - new Date(msg.createdAt)) <= 30 * 60 * 1000 : false;
                // For DMs: no tick = local only, single = on server, grey double = delivered, blue double = read
                const canEditDeleteThisMsg = msgIsOwn && (
                  Boolean(msg.replyToMessageId) || !msg.isRead || isWithin30Minutes
                );

                const msgTickState = getMessageTickState(msg, { isDirectMessage, isOwn: bubbleOwn });
                const translationPending = isChatTranslationPending(
                  msg,
                  preferredLanguage,
                  msg.senderId === currentUserId,
                );
                const bubbleTimeLabel = formatBubbleTime(msg.createdAt || msg.timestamp);
                
                // Find the original message if this is a reply
                // Show reply context for any message that has a replyToMessageId
                const originalMessage = msg.replyToMessageId 
                  ? allMessages.find(m => m.id === msg.replyToMessageId)
                  : null;
                
                // Debug logging for reply messages
                if (msg.replyToMessageId && !originalMessage) {
                  console.log('⚠️ Reply message found but original message not in allMessages:', {
                    replyMessageId: msg.id,
                    replyToMessageId: msg.replyToMessageId,
                    allMessagesCount: allMessages.length,
                    allMessageIds: allMessages.map(m => m.id).slice(0, 10)
                  });
                }
                
                const rawResolvedUrl = msg.linkPreview?.url
                  || extractUrlFromText(msg.content);
                const resolvedMessageUrl = rawResolvedUrl && !isInternalAppTransportUrl(rawResolvedUrl)
                  ? rawResolvedUrl
                  : extractUrlFromText(msg.content);
                
                // Create a linkify function for this specific message
                const linkifyTextForMessage = (text) => {
                  if (!text) return '';

                  const fullUrl = resolvedMessageUrl || extractUrlFromText(text);
                  if (fullUrl && text.includes(fullUrl.replace(/^https?:\/\//i, '').split('/')[0])) {
                    const displayUrl = fullUrl.length > 50 ? `${fullUrl.substring(0, 47)}...` : fullUrl;
                    const linkClassName = msgBubbleTextOnLight
                      ? 'text-primary-600 dark:text-primary-400 hover:underline break-all'
                      : 'text-white/90 hover:text-white hover:underline break-all transition-colors';
                    const plain = text.trim();
                    if (plain.replace(/\s+/g, '') === fullUrl.replace(/\s+/g, '') || plain.includes(fullUrl)) {
                      return (
                        <a
                          href={fullUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={linkClassName}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {displayUrl}
                        </a>
                      );
                    }
                  }
                  
                  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*)/g;
                  
                  const parts = [];
                  let lastIndex = 0;
                  let match;
                  
                  while ((match = urlRegex.exec(text)) !== null) {
                    if (match.index > lastIndex) {
                      parts.push(text.substring(lastIndex, match.index));
                    }
                    
                    let url = match[0];
                    let displayUrl = url;
                    
                    if (!url.startsWith('http://') && !url.startsWith('https://')) {
                      url = url.startsWith('www.') ? 'https://' + url : 'https://' + url;
                    }
                    
                    if (displayUrl.length > 50) {
                      displayUrl = displayUrl.substring(0, 47) + '...';
                    }
                    
                    const linkClassName = msgBubbleTextOnLight
                      ? 'text-primary-600 dark:text-primary-400 hover:underline break-all'
                      : 'text-white/90 hover:text-white hover:underline break-all transition-colors';
                    
                    parts.push(
                      <a
                        key={match.index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClassName}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {displayUrl}
                      </a>
                    );
                    
                    lastIndex = match.index + match[0].length;
                  }
                  
                  if (lastIndex < text.length) {
                    parts.push(text.substring(lastIndex));
                  }
                  
                  const result = parts.length > 0 ? parts : text;
                  if (!searchHighlightQuery?.trim()) {
                    return result;
                  }
                  if (typeof result === 'string') {
                    return renderHighlightedSearchText(result, searchHighlightQuery, `link-${messageKey}`);
                  }
                  if (Array.isArray(result)) {
                    return result.map((part, partIndex) => (
                      typeof part === 'string'
                        ? renderHighlightedSearchText(part, searchHighlightQuery, `link-${messageKey}-${partIndex}`)
                        : part
                    ));
                  }
                  return result;
                };

                const actionOnLeftOfBubble = isInThread
                  ? false
                  : isCallGroup
                    ? (bubbleOwn || callGroupRightAligned)
                    : bubbleOwn;
                const actionBarPositionClasses = actionOnLeftOfBubble ? 'right-full mr-1' : 'left-full ml-1';
                const actionBarVisibilityClasses = (hoveredMessageId === messageKey || showReactions === messageKey || openMenuId === messageKey) ? 'opacity-100' : 'opacity-0';
                const inlineMenuBtnClass = (hasOnlyAttachments || (msg.attachments?.length > 0 && !hasContent))
                  ? 'text-white bg-black/50 hover:bg-black/65 backdrop-blur-[2px] shadow-sm'
                  : msgBubbleTextOnLight
                    ? 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100/90 dark:hover:bg-gray-600/90'
                    : 'text-white/90 hover:text-white hover:bg-white/20';

                const preferMenuAbove = (isGrouped && isLastInGroup) || isLastInTimeline;

                const messageMenuDropdown = openMenuId === messageKey && menuPortalPosition && ReactDOM.createPortal(
                          <div
                            ref={menuPortalRef}
                            data-message-menu
                            className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 w-48 z-50 max-h-[min(70vh,28rem)] overflow-y-auto"
                            style={{
                              position: 'fixed',
                              top: menuPortalPosition.top,
                              left: menuPortalPosition.left,
                              zIndex: 99999,
                            }}
                          >
                            <div className="py-1">
                              {/* Emoji/Reaction */}
                              <div className="relative">
                        <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openReactionPicker(messageKey);
                                    setOpenMenuId(null);
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span>Add Reaction</span>
                                </button>
                              </div>

                              {(() => {
                                const linkedTask = findLinkedTaskForMessage(msg);
                                const namedTask = linkedTask
                                  && (linkedTask.title || '').trim()
                                  && !isWorkflowShellTask(linkedTask);
                                const isTaskItem = messageIsTaskWorkItem(msg.reactions)
                                  || Boolean(namedTask && !messageIsRaisedWorkflow(msg.reactions));
                                const isRaisedWorkflow = messageIsRaisedWorkflow(msg.reactions);
                                if (msg.status === 'pending' || msg.status === 'failed') return null;
                                if (!isRaisedWorkflow && !isTaskItem) return null;
                                return (
                                <>
                                  {isRaisedWorkflow && (
                                  <button
                                    onClick={() => {
                                      handleOpenTrackDialog(msg);
                                      setOpenMenuId(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                  >
                                    <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                    </svg>
                                    <span>{msg.workflowTrackId ? 'Edit Track ID' : 'Add Track ID'}</span>
                                  </button>
                                  )}
                                  {isRaisedWorkflow && !getMessageWorkflowVisual(msg.reactions, msg.workflowByCategory)?.isClosed && (
                                    <button
                                      onClick={() => handleMarkWorkflowComplete(msg)}
                                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                      <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      <span>Mark Complete</span>
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleOpenAssignPicker(messageKey);
                                      setOpenMenuId(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                  >
                                    <svg className="w-5 h-5 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    <span>Assign to</span>
                                  </button>
                                </>
                                );
                              })()}

                              {msg.status !== 'pending' && msg.status !== 'failed' && (
                                <button
                                  onClick={() => {
                                    handleOpenCustomTagInput(messageKey);
                                    setOpenMenuId(null);
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                  <svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
                                  </svg>
                                  <span>Add tag</span>
                                </button>
                              )}

                              {!isInThread && (
                              <button
                                onClick={() => {
                                  onReply && onReply(msg);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              >
                                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                </svg>
                                <span>Reply</span>
                              </button>
                              )}

                              {/* Edit - own messages only, unread */}
                              {canEditDeleteThisMsg && !isEditingThisMsg && msg.status !== 'pending' && msg.status !== 'failed' && (
                                <button
                                  onClick={() => {
                                    handleEdit(msg);
                                    setOpenMenuId(null);
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                  <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                                  <span>Edit</span>
                        </button>
                              )}

                              {/* Delete - own messages only, unread */}
                              {canEditDeleteThisMsg && msg.status !== 'pending' && msg.status !== 'failed' && (
                        <button
                                  onClick={() => {
                                    handleDelete(msg.id);
                                    setOpenMenuId(null);
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                                  <span>Delete</span>
                                </button>
                              )}

                              {/* Copy — for events, only when invite-others is allowed */}
                              {canShareEvent && (
                              <button
                                onClick={() => {
                                  handleCopyMessage(msg);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              >
                                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h2m0 12h2a2 2 0 002-2v-2m0 12V6a2 2 0 00-2-2h-2m-4-1h8m-4 0V4a2 2 0 00-2-2h-2M8 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2M8 4z" />
                                </svg>
                                <span>Copy message</span>
                              </button>
                              )}

                              {msg.id && msg.status !== 'pending' && msg.status !== 'failed' && (
                              <button
                                onClick={() => {
                                  handleCopyThreadLink(msg);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              >
                                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                <span>Copy thread link</span>
                              </button>
                              )}

                              {/* Map Task */}
                              <button
                                onClick={() => {
                                  handleOpenMapTaskDialog(msg);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              >
                                <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                </svg>
                                <span>Map to Task</span>
                              </button>

                              {/* Create Task */}
                              <button
                                onClick={() => {
                                  handleOpenTaskDialog(msg);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              >
                                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span>Add to Task</span>
                              </button>

                              {/* Forward — for events, only when invite-others is allowed */}
                              {canShareEvent && (
                              <button
                                onClick={() => {
                                  onForward && onForward(msg);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              >
                                <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                </svg>
                                <span>Forward</span>
                              </button>
                              )}

                              {/* Bookmark */}
                              <button
                                onClick={() => {
                                  handleSaveMessage(msg.id);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              >
                                {isMessageSaved(msg.id) ? (
                                  <>
                                    <BookmarkIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" filled />
                                    <span>Remove bookmark</span>
                                  </>
                                ) : (
                                  <>
                                    <BookmarkIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                    <span>Bookmark</span>
                                  </>
                                )}
                        </button>

                              <button
                                onClick={() => {
                                  setVaultSaveMessage(msg);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              >
                                <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                <span>Save to Vault</span>
                              </button>
                      </div>
                          </div>,
                          document.body
                );

                const messageActionBtnClass =
                  'p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors flex-shrink-0';

                const reactionActionButton = (
                  <div
                    ref={(el) => {
                      if (messageKey) {
                        reactionPickerRefs.current[messageKey] = el;
                      }
                    }}
                    className="relative flex-shrink-0"
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openReactionPicker(messageKey);
                      }}
                      className={messageActionBtnClass}
                      title="Add reaction"
                      aria-label="Add reaction"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </div>
                );

                const replyActionButton = !isInThread && onReply ? (
                  <button
                    type="button"
                    onClick={() => onReply(msg)}
                    className={messageActionBtnClass}
                    title="Reply"
                    aria-label="Reply"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                  </button>
                ) : null;

                const messageHoverActionBar = !isActiveCallMessage && (
                  <div className={`absolute top-1.5 flex items-center gap-1 z-20 transition-opacity duration-150 ${actionBarVisibilityClasses} ${actionBarPositionClasses}`}>
                    {reactionActionButton}
                    {replyActionButton}
                  </div>
                );

                const activeCallActionBar = isActiveCallMessage && !isEditingThisMsg && (
                  <div className={`absolute top-1.5 right-2 z-[100] flex items-center gap-0.5 transition-opacity duration-150 pointer-events-auto ${actionBarVisibilityClasses}`}>
                    {reactionActionButton}
                    {replyActionButton}
                    <button
                      ref={(el) => {
                        menuButtonRefs.current[messageKey] = el;
                      }}
                      type="button"
                      onClick={() => toggleMessageMenu(messageKey, preferMenuAbove)}
                      className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors flex-shrink-0"
                      title="More options"
                      aria-label="More options"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {messageMenuDropdown}
                  </div>
                );

                const messageInlineMenu = !isEditingThisMsg && !isActiveCallMessage && (
                  <div className={`absolute top-1.5 right-2 z-[100] message-menu-container transition-opacity duration-150 pointer-events-auto ${actionBarVisibilityClasses}`}>
                    <button
                      ref={(el) => {
                        menuButtonRefs.current[messageKey] = el;
                      }}
                      type="button"
                      onClick={() => toggleMessageMenu(messageKey, preferMenuAbove)}
                      className={`p-0.5 rounded transition-colors ${inlineMenuBtnClass}`}
                      title="More options"
                      aria-label="More options"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {messageMenuDropdown}
                  </div>
                );

                const isActiveSearchMatch = Boolean(
                  activeSearchMatchMessageId
                  && (msg.id === activeSearchMatchMessageId || msg.tempId === activeSearchMatchMessageId),
                );

                const isAssignDropdownOpenForMessage = assignDropdownOpenMessageKey === messageKey;

                const handleMappingMessageClick = (e, mapMsg) => {
                  if (!chatMappingActive || !onMapMessage) return;
                  if (e.target.closest('button, a, input, textarea, select, [data-message-menu], [data-workflow-assign-dropdown], [data-workflow-assign-dropdown-panel], [data-reaction-picker], [data-open-work-item]')) {
                    return;
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  onMapMessage(mapMsg);
                };

                const isAttendanceEntry = Boolean(msg.attendanceInfo?.attendanceEventId);
                if (isAttendanceEntry) {
                  return (
                    <div key={messageKey} className="relative w-full py-1">
                      <AttendanceTimelineButton message={msg} />
                      <span className="block text-center text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                        {formatTime(msg.createdAt || msg.timestamp)}
                      </span>
                    </div>
                  );
                }

                if (msg.notificationInfo) {
                  const info = msg.notificationInfo;
                  const rawLabel = (info.label || msg.content || 'Update').replace(/\s+/g, ' ').trim();
                  const isReactionEvent = String(info.eventType || '').toUpperCase() === 'MESSAGE_REACTION';
                  const toIndex = rawLabel.lastIndexOf(' to ');
                  const label = isReactionEvent
                    ? formatReactionToMessageLine(
                      info.statusLabel || info.reactionKey,
                      toIndex >= 0 ? rawLabel.slice(toIndex + 4).trim() : rawLabel,
                      rawLabel
                    )
                    : rawLabel;
                  const isTaskEvent = String(info.eventType || '').startsWith('TASK_');
                  const canOpen = isTaskEvent
                    ? Boolean(info.taskId)
                    : Boolean(info.sourceChannelId && info.sourceMessageId);
                  return (
                    <div key={messageKey} className="relative w-full py-0.5">
                      <button
                        type="button"
                        disabled={!canOpen}
                        onClick={() => {
                          if (!canOpen) return;
                          window.dispatchEvent(new CustomEvent('workloop:open-notification', {
                            detail: {
                              eventType: info.eventType,
                              sourceChannelId: info.sourceChannelId,
                              sourceMessageId: info.sourceMessageId,
                              taskId: info.taskId,
                            },
                          }));
                        }}
                        className={`block w-full text-center text-[11px] leading-4 px-3 truncate ${
                          canOpen
                            ? 'text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer'
                            : 'text-gray-400 dark:text-gray-500 cursor-default'
                        }`}
                        title={label}
                      >
                        {label}
                      </button>
                    </div>
                  );
                }

                const threadReplies = onViewThread ? getRecursiveThreadReplies(msg.id, allMessages) : [];
                const hasThreadReplies = threadReplies.length > 0;
                const showInlineReplies = hasThreadReplies && canShowInlineThreadReplies(threadReplies);

                const timelineReplyHint = onViewThread && msg.replyToMessageId
                  ? `Reply to ${originalMessage?.senderName || 'thread'}`
                  : null;

                return (
                  <div
                    key={messageKey}
                    className={`relative overflow-visible w-full flex flex-col ${isWorkTimelineCardMessage(msg) ? '' : 'max-w-full min-w-0'}`}
                    onMouseEnter={() => {
                      // Don't open the reply thread while the reaction picker or menu is open —
                      // that reflow hides/misplaces the picker.
                      if (showReactions != null || openMenuId != null) return;
                      // Short inline replies are already visible under the bubble.
                      if (hasThreadReplies && !showInlineReplies && onPreviewThread) onPreviewThread(msg.id);
                    }}
                    onMouseLeave={() => {
                      if (hasThreadReplies && !showInlineReplies && onPreviewThread) onPreviewThread(null);
                    }}
                  >
                  {timelineReplyHint ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewThread(msg.id);
                      }}
                      className={`mb-0.5 max-w-full truncate text-[11px] leading-tight text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 ${
                        bubbleOwn && !isInThread ? 'self-end text-right' : 'self-start text-left'
                      }`}
                      title="Open thread"
                    >
                      {timelineReplyHint}
                    </button>
                  ) : null}
                  <MessageSectionShell
                    msg={msg}
                    isOwn={bubbleOwn}
                    estimateMinutes={getMessageUrgentEstimateMinutes(msg, allTasks)}
                    isActiveCallMessage={isActiveCallMessage}
                    hasEvent={hasEvent || hasAppointment}
                    transparentBubble={(hasShare || hasThreadShare) && !hasContent}
                    isActiveSearchMatch={isActiveSearchMatch}
                    isAssignDropdownOpenForMessage={isAssignDropdownOpenForMessage}
                    chatMappingActive={chatMappingActive}
                    sectionRoundingClass={sectionRoundingClass}
                    onClick={chatMappingActive ? (e) => handleMappingMessageClick(e, msg) : undefined}
                    role={chatMappingActive ? 'button' : undefined}
                    title={chatMappingActive ? 'Map this message' : undefined}
                    messageRef={(el) => {
                      if (reactionMessageId && messageRefs?.current) {
                        messageRefs.current[reactionMessageId] = el;
                      }
                    }}
                    onMouseEnter={() => setHoveredMessageId(messageKey)}
                    onMouseLeave={() => setHoveredMessageId(null)}
                  >
                    {isActiveCallMessage && (
                      <LiveCallBubbleBackdrop
                        active
                        messageId={msg.id || msg.tempId}
                      />
                    )}
                    <div className={`relative overflow-visible ${msgWorkflowVisual || isActiveCallMessage ? 'z-[1]' : ''}`}>
                    {/* Separator between grouped messages */}
                    {msgIndex > 0 && (
                      <div
                        className={`w-full ${msgBubbleTextOnLight
                          ? 'border-t border-gray-300/50 dark:border-gray-500/50'
                          : 'border-t border-white/20'
                          }`}
                      />
                    )}

                    {messageHoverActionBar}
                    {activeCallActionBar}

                    {isEditingThisMsg ? (
                      <div className="space-y-1.5 relative">
                        <div className="bg-white dark:bg-gray-800 rounded-lg relative overflow-hidden" style={{ zIndex: 1, maxHeight: '300px' }}>
                          <ReactQuill
                            ref={editQuillRef}
                            theme="snow"
                            value={editContent}
                            onChange={setEditContent}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Edit your message..."
                            className="bg-white dark:bg-gray-800"
                            style={{
                              minHeight: '80px',
                              maxHeight: '250px',
                            }}
                          />
                        </div>
                        <div className="flex items-center space-x-2 relative" style={{ zIndex: 10, position: 'relative' }}>
                          <button
                            onClick={handleSaveEdit}
                            className="px-2.5 py-1 text-[11px] bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors cursor-pointer"
                            type="button"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="px-2.5 py-1 text-[11px] bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                            type="button"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : isWorkTimelineCardMessage(msg) ? (
                      <WorkflowTimelineCard
                        msg={msg}
                        linkedTask={(() => {
                          const linked = findLinkedTaskForMessage(msg);
                          if (!linked || isWorkflowShellTask(linked)) return null;
                          return linked;
                        })()}
                        variant={isWorkflowChatBubble(msg) ? 'workflow' : 'task'}
                        currentUserId={currentUserId}
                        timeLabel={bubbleTimeLabel}
                        onReactionClick={(emoji) => handleReactionClick(reactionMessageId, emoji)}
                        onOpen={() => handleOpenWorkItemPopup(msg)}
                        tagInputOpen={tagInputMessageKey === messageKey}
                        tagInputDraft={tagInputDraft}
                        tagInputRef={tagInputMessageKey === messageKey ? tagInputRef : null}
                        onOpenTagInput={() => handleOpenCustomTagInput(messageKey)}
                        onTagDraftChange={setTagInputDraft}
                        onSubmitTag={() => handleSubmitCustomTag(reactionMessageId, messageKey)}
                        onCloseTagInput={handleCloseCustomTagInput}
                        onRemoveTag={(tagId) => handleRemoveCustomTag(reactionMessageId, tagId)}
                        extra={msg.attachments && msg.attachments.length > 0 ? (
                          <div className="mt-3">
                            <MessageAttachmentsDisplay
                              attachments={msg.attachments}
                              attachmentGroupName={msg.attachmentGroupName}
                              onViewImage={(images, index) =>
                                setViewingGallery({
                                  images: (images || []).map((item) => ({
                                    ...item,
                                    messageId: item.messageId || msg.id,
                                    channelId: item.channelId || msg.channelId,
                                    senderId: item.senderId || msg.senderId,
                                  })),
                                  index,
                                })
                              }
                              onDownload={handleDownload}
                              onViewAll={(all) => setViewingAllAttachments(
                                (all || []).map((item) => ({
                                  ...item,
                                  messageId: item.messageId || msg.id,
                                  channelId: item.channelId || msg.channelId,
                                  senderId: item.senderId || msg.senderId,
                                }))
                              )}
                              onOpenGroup={(groupAttachments, index) =>
                                setViewingGroup({
                                  attachments: (groupAttachments || []).map((item) => ({
                                    ...item,
                                    messageId: item.messageId || msg.id,
                                    channelId: item.channelId || msg.channelId,
                                    senderId: item.senderId || msg.senderId,
                                  })),
                                  groupName: msg.attachmentGroupName || '',
                                  index: index || 0,
                                })
                              }
                            />
                          </div>
                        ) : null}
                        assigneeSlot={(
                          <div
                            ref={assignPickerMessageKey === messageKey ? assignPickerRef : null}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <WorkflowAssigneeDropdown
                              layout="person"
                              hideLabel
                              bare
                              caption="Assigned to"
                              users={assignableUsers}
                              selectedUserId={resolveMessageAssignee(msg, findLinkedTaskForMessage(msg)).id}
                              selectedUserName={resolveMessageAssignee(msg, findLinkedTaskForMessage(msg)).name}
                              isOpen={assignDropdownOpenMessageKey === messageKey}
                              searchQuery={assignSearchQuery}
                              onToggleOpen={(open) => {
                                setAssignPickerMessageKey(open ? messageKey : null);
                                setAssignDropdownOpenMessageKey(open ? messageKey : null);
                                if (!open) setAssignSearchQuery('');
                              }}
                              onSearchChange={setAssignSearchQuery}
                              onSelect={(userId) => handleAssignWorkflow(msg, userId)}
                            />
                          </div>
                        )}
                      />
                    ) : (
                      <div className={`${isSingleImageOnly ? 'p-0' : hasOnlyAttachments ? 'p-1' : (hasEvent || hasAppointment) ? 'py-1 px-1' : 'py-[0.40rem] pl-3 pr-3'} ${!hasOnlyAttachments && !isSingleImageOnly && isActiveCallMessage ? 'pr-24' : ''} w-full grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-2`}>
                        <div className="min-w-0">
                        {hasContent && (
                          <div className="text-sm">
                            {isCallNoteMessage(msg) && (
                              <div className="flex items-center gap-2 mb-1.5">
                                {isCallGroup && (
                                  <button
                                    type="button"
                                    onClick={() => openUserProfile(msg.senderId)}
                                    className="flex-shrink-0 rounded-full hover:opacity-90 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/40 transition-opacity"
                                    title={`View ${msg.senderName || 'user'}'s profile`}
                                    aria-label={`View ${msg.senderName || 'user'}'s profile`}
                                  >
                                    <PresignedAvatar
                                      fileKey={msg.senderProfilePicture}
                                      name={msg.senderName}
                                      className="w-5 h-5 rounded-full object-cover"
                                      fallbackClassName="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center text-white text-[9px] font-medium"
                                    />
                                  </button>
                                )}
                                {isCallGroup && (
                                  <span className={`text-xs font-semibold ${msgBubbleTextOnLight ? 'text-gray-800 dark:text-gray-100' : 'text-white'}`}>
                                    {msg.senderName || 'User'}
                                  </span>
                                )}
                                <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded ${
                                  msgBubbleTextOnLight
                                    ? 'text-indigo-700 dark:text-indigo-300 bg-indigo-100/80 dark:bg-indigo-900/40'
                                    : 'text-white bg-white/20'
                                }`}>
                                  Call note
                              </span>
                          </div>
                        )}
                            {/* Truncation logic for long messages */}
                            {msg.sensitive ? (
                              <button
                                type="button"
                                onClick={() => setSensitiveUnlockMessage(msg)}
                                className={`w-full text-left rounded-lg px-2 py-2 ${
                                  msgBubbleTextOnLight
                                    ? 'bg-rose-50 dark:bg-rose-950/40'
                                    : 'bg-white/15'
                                }`}
                              >
                                <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide ${
                                  msgBubbleTextOnLight ? 'text-rose-700 dark:text-rose-300' : 'text-white'
                                }`}>
                                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                  </svg>
                                  Sensitive
                                </span>
                                <p className={`mt-1 text-sm font-medium ${msgBubbleTextOnLight ? 'text-gray-800 dark:text-gray-100' : 'text-white'}`}>
                                  {msg.sensitiveTitle || 'Protected message'}
                                </p>
                                <p className={`mt-1.5 text-[11px] leading-snug ${
                                  msgBubbleTextOnLight ? 'text-rose-800/80 dark:text-rose-200/80' : 'text-white/85'
                                }`}>
                                  {sensitiveAuthLines(msg)[0]}
                                </p>
                                {Array.isArray(msg.attachments) && msg.attachments.length > 0
                                  && !msg.attachments.some((item) => item.fileUrl) ? (
                                  <ul className="mt-2 space-y-1">
                                    {msg.attachments.map((item, index) => (
                                      <li
                                        key={item.id || item.fileUrl || `${item.fileName}-${index}`}
                                        className={`flex items-center gap-1.5 truncate text-[11px] ${
                                          msgBubbleTextOnLight ? 'text-gray-600 dark:text-gray-300' : 'text-white/80'
                                        }`}
                                      >
                                        <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                        </svg>
                                        <span className="truncate">{item.fileName || item.fileType || 'Attachment'}</span>
                                      </li>
                                    ))}
                                  </ul>
                                ) : null}
                              </button>
                            ) : (() => {
                              const content = messageBodyForViewer(
                                msg,
                                preferredLanguage,
                                msg.senderId === currentUserId,
                              ).trim();
                              const normalizedContent = unwrapEscapedHtmlContent(content);
                              const isRichText = isRichTextMessageContent(content);
                              const strippedText = isRichText ? stripHtmlTags(normalizedContent) : content;
                              
                              // Thresholds: 600 characters or ~8 lines
                              const charThreshold = 600;
                              const lineThreshold = 8;
                              const lineCount = strippedText.split('\n').length;
                              const isLong = strippedText.length > charThreshold || lineCount > lineThreshold;
                              const isExpanded = expandedMessages[msg.id];

                              return (
                                <div className="relative group/content">
                                  <div 
                                    className={`relative transition-all duration-300 ${!isExpanded && isLong ? 'max-h-[220px] overflow-hidden' : 'max-h-none'}`}
                                  >
                                    {isRichText ? (
                                      <MessageMentionContent
                                        isRichText
                                        content={content}
                                        normalizedContent={normalizedContent}
                                        resolvedMessageUrl={resolvedMessageUrl}
                                        mentionOnLight={msgBubbleTextOnLight}
                                        searchHighlightQuery={searchHighlightQuery}
                                className="ql-editor-content"
                                style={{
                                  fontSize: '0.875rem',
                                          lineHeight: '1.4',
                                          color: msgBubbleTextOnLight ? 'inherit' : 'rgba(255, 255, 255, 0.9)',
                                }}
                              />
                            ) : (
                                      <MessageMentionContent
                                        content={content}
                                        mentionOnLight={msgBubbleTextOnLight}
                                        searchHighlightQuery={searchHighlightQuery}
                                        className={`whitespace-pre-wrap break-words leading-tight ${msgBubbleTextOnLight ? '' : 'text-white'}`}
                                        renderTextSegment={linkifyTextForMessage}
                                      />
                                    )}
                                    
                                    {/* Fade effect for truncated messages */}
                                    {!isExpanded && isLong && (
                                      <div className={`absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t ${
                                        msgWorkflowVisual
                                          ? (msgWorkflowVisual.isClosed
                                            ? 'from-green-50 dark:from-green-950/40'
                                            : 'from-yellow-50 dark:from-yellow-950/35')
                                          : msgBubbleTextOnLight
                                            ? 'from-gray-100 dark:from-gray-700'
                                            : 'from-red-500'
                                      } to-transparent pointer-events-none`} />
                                    )}
                                  </div>
                                  
                                  {isLong && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedMessages(prev => ({
                                          ...prev,
                                          [msg.id]: !prev[msg.id]
                                        }));
                                      }}
                                      className={`mt-0.5 text-[11px] font-bold hover:underline focus:outline-none transition-colors ${
                                        msgBubbleTextOnLight
                                          ? 'text-primary-600 dark:text-primary-400'
                                          : 'text-white/90 hover:text-white'
                                      }`}
                                    >
                                      {isExpanded ? 'Read Less' : 'Read More'}
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                        {msg.isEdited && (
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 italic ml-1">(edited)</span>
                        )}

                        {/* Retry button for failed optimistic messages */}
                        {msg.isOptimistic && msg.status === 'failed' && onRetryMessage && (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs opacity-75">Failed to send</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onRetryMessage(msg.tempId, msg);
                              }}
                              className="px-2 py-1 text-xs bg-white dark:bg-gray-800 text-red-600 dark:text-red-400 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-1"
                              title="Retry sending message"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Retry
                            </button>
                          </div>
                        )}

                        {/* Meeting card */}
                        {(msg.meetingInfo?.joinUrl || msg.meetingInfo?.provider === 'livekit' || msg.meetingInfo?.mode === 'direct') && (
                          <div className={`w-full ${hasOnlyAttachments && !hasContent ? 'p-2' : hasContent ? 'mt-2' : 'py-2 px-4'}`}>
                            <MeetingTimelineCard
                              message={msg}
                              currentUserId={currentUserId}
                              onNotePosted={onMeetingMessageUpdated}
                              onMeetingUpdated={onMeetingMessageUpdated}
                              notesExpanded={expandCallNotesMeetingId === msg.id}
                              channelNoteMessages={getMeetingNoteMessages(allMessages, msg.id)}
                              reserveTopActionsSpace={isActiveCallMessage}
                              onJoinDirectCall={onJoinDirectCall}
                                  />
                                </div>
                              )}

                        {/* Calendar appointment invite */}
                        {msg.appointmentInfo?.appointmentId && (
                          <div className={`w-full ${hasOnlyAttachments && !hasContent ? 'p-2' : hasContent ? 'mt-2' : 'py-2 px-4'}`}>
                            <AppointmentTimelineCard
                              message={msg}
                              currentUserId={currentUserId}
                              onUpdated={onMeetingMessageUpdated}
                            />
                          </div>
                        )}

                        {msg.eventInfo?.eventId && (
                          <div className="w-full py-0.5">
                            <EventTimelineCard
                              message={msg}
                              currentUserId={currentUserId}
                              onUpdated={onMeetingMessageUpdated}
                              onEditEvent={onEditEvent}
                              onCloneEvent={onCloneEvent}
                              cloning={cloningEventId === msg.eventInfo?.eventId}
                            />
                          </div>
                        )}

                        {hasShare && (
                          <div className={`w-full ${hasContent ? 'mt-2' : 'py-0.5'}`}>
                            <WorkItemShareCard
                              share={share}
                              onOpen={() => {
                                window.dispatchEvent(new CustomEvent('workloop:open-shared-item', { detail: share }));
                              }}
                              onCopyLink={() => handleCopyShareLink(
                                buildWorkItemShareUrl(share),
                                'Link copied',
                              )}
                            />
                          </div>
                        )}

                        {hasThreadShare && (
                          <div className={`w-full ${hasContent ? 'mt-2' : 'py-0.5'}`}>
                            <WorkItemShareCard
                              share={{ title: 'Open in chat' }}
                              typeLabel="Thread"
                              onOpen={() => {
                                window.dispatchEvent(new CustomEvent('workloop:go-to-message', {
                                  detail: {
                                    channelId: threadShare.channelId || msg.channelId || channelIdProp,
                                    messageId: threadShare.messageId,
                                    threadId: threadShare.threadId || undefined,
                                    replyToMessageId: threadShare.threadId && threadShare.threadId !== threadShare.messageId
                                      ? threadShare.threadId
                                      : undefined,
                                    openThread: Boolean(threadShare.threadId),
                                  },
                                }));
                              }}
                              onCopyLink={() => handleCopyShareLink(
                                buildChatThreadShareUrl({
                                  channelId: threadShare.channelId || msg.channelId || channelIdProp,
                                  messageId: threadShare.messageId,
                                  threadId: threadShare.threadId,
                                }),
                                'Thread link copied',
                              )}
                            />
                          </div>
                        )}

                        {/* Link Preview for this message */}
                        {!msg.sensitive && msg.linkPreview && !hasShare && !hasThreadShare && (
                          <div className={hasOnlyAttachments ? '' : 'mt-2'}>
                            <TimelineLinkCard
                              link={{
                                ...msg.linkPreview,
                                url: resolvedMessageUrl || msg.linkPreview.url,
                              }}
                            />
                          </div>
                        )}
                        
                        {/* Attachments for this message */}
                        {msg.attachments && msg.attachments.length > 0
                          && (!msg.sensitive || msg.attachments.some((item) => item.fileUrl)) && (
                          <div className={hasOnlyAttachments ? '' : 'mt-2'}>
                            <MessageAttachmentsDisplay
                              attachments={msg.attachments}
                              attachmentGroupName={msg.attachmentGroupName}
                              onViewImage={(images, index) =>
                                setViewingGallery({
                                  images: (images || []).map((item) => ({
                                    ...item,
                                    messageId: item.messageId || msg.id,
                                    channelId: item.channelId || msg.channelId,
                                    senderId: item.senderId || msg.senderId,
                                  })),
                                  index,
                                })
                              }
                              onDownload={handleDownload}
                              onViewAll={(all) => setViewingAllAttachments(
                                (all || []).map((item) => ({
                                  ...item,
                                  messageId: item.messageId || msg.id,
                                  channelId: item.channelId || msg.channelId,
                                  senderId: item.senderId || msg.senderId,
                                }))
                              )}
                              onOpenGroup={(groupAttachments, index) =>
                                setViewingGroup({
                                  attachments: (groupAttachments || []).map((item) => ({
                                    ...item,
                                    messageId: item.messageId || msg.id,
                                    channelId: item.channelId || msg.channelId,
                                    senderId: item.senderId || msg.senderId,
                                  })),
                                  groupName: msg.attachmentGroupName || '',
                                  index: index || 0,
                                })
                              }
                            />
                            <VaultZipContentsNote
                              attachments={msg.attachments}
                              onLight={msgBubbleTextOnLight}
                            />
                                        </div>
                        )}

                                      </div>
                        {bubbleTimeLabel || msgTickState !== 'none' || translationPending ? (
                          <span
                            className={`shrink-0 justify-self-end inline-flex flex-col items-end gap-0.5 text-[10px] leading-none tabular-nums whitespace-nowrap ${
                              msgBubbleTextOnLight ? 'text-gray-400 dark:text-gray-500' : 'text-white/75'
                            }`}
                          >
                            {translationPending ? (
                              <span
                                className={`h-3 w-3 animate-spin rounded-full border-[1.5px] ${
                                  msgBubbleTextOnLight
                                    ? 'border-sky-400 border-t-transparent'
                                    : 'border-white/80 border-t-transparent'
                                }`}
                                aria-label="Translating"
                                title="Translating"
                              />
                            ) : null}
                            <span className="inline-flex items-center">
                              {bubbleTimeLabel}
                              {msgTickState !== 'none' ? (
                                <DeliveryTickIcon state={msgTickState} onLight={msgBubbleTextOnLight} />
                              ) : null}
                            </span>
                          </span>
                        ) : null}

                                        </div>
                    )}

                    {msg.status !== 'pending' && msg.status !== 'failed' && !isWorkTimelineCardMessage(msg) && (() => {
                      const { workflow, free } = partitionMessageReactions(
                        mergeReactionsWithWorkflowCategory(msg.reactions || [], msg.workflowByCategory)
                      );
                      const hasMetaContent = (msg.reactions?.length > 0)
                        || Boolean(msg.workflowTrackId)
                        || Boolean(msg.templateInfo?.estimateTimeInMinutes)
                        || (msg.customTags?.length > 0);
                      const isTagInputOpen = tagInputMessageKey === messageKey;
                      const showMetaRow = hasMetaContent || isTagInputOpen;
                      if (!showMetaRow) return null;

                      const jiraTagClasses = !msgBubbleTextOnLight
                        ? 'bg-white/25 text-white border-white/20 hover:bg-white/35'
                        : 'bg-slate-100 dark:bg-slate-700/80 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:bg-slate-200/80 dark:hover:bg-slate-600/80';
                      const addTagBtnClasses = !msgBubbleTextOnLight
                        ? 'border-white/35 text-white/85 hover:bg-white/15 hover:border-white/50'
                        : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200';
                      const tagInputClasses = !msgBubbleTextOnLight
                        ? 'bg-white/15 border-white/30 text-white placeholder-white/60 focus:ring-white/40'
                        : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-primary-500/60';

                      return (
                        <div
                          className={`group/meta relative flex flex-wrap gap-1 items-center mt-0.5 mb-0.5 overflow-visible ${
                            isAssignDropdownOpenForMessage ? 'z-[200]' : 'z-[1]'
                          }`}
                          style={{
                            paddingLeft: 10,
                            paddingBottom: 4,
                            minHeight: 'auto',
                            width: isCallGroup ? 'auto' : '90%',
                          }}
                        >
                          {workflow.map((reaction, reactionIndex) => (
                            <MessageReactionBadge
                              key={`wf-${messageKey}-${reactionIndex}`}
                              reactionId={reaction.emoji}
                              count={reaction.userIds?.length || 0}
                              isOwn={!msgBubbleTextOnLight}
                              isWorkflow
                              onClick={() => handleReactionClick(reactionMessageId, reaction.emoji)}
                            />
                          ))}
                          {free.map((reaction, reactionIndex) => {
                            const isActive = currentUserId != null
                              && Array.isArray(reaction.userIds)
                              && reaction.userIds.some((id) => String(id) === String(currentUserId));
                            return (
                              <MessageReactionBadge
                                key={`free-${messageKey}-${reactionIndex}`}
                                reactionId={reaction.emoji}
                                count={reaction.userIds?.length || 0}
                                isOwn={!msgBubbleTextOnLight}
                                isActive={isActive}
                                onClick={() => handleReactionClick(reactionMessageId, reaction.emoji)}
                              />
                            );
                          })}
                          {msg.workflowTrackId && (
                                          <button
                              type="button"
                              onClick={() => {
                                if (msg.workflowTrackLink) {
                                  window.open(msg.workflowTrackLink, '_blank', 'noopener,noreferrer');
                                }
                              }}
                              className={`flex items-center gap-1 min-h-6 px-2 py-0.5 rounded-full text-xs border transition-colors flex-shrink-0 ${
                                !msgBubbleTextOnLight
                                  ? 'bg-white/20 dark:bg-white/10 hover:bg-white/30 text-white border-white/30'
                                  : 'bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200 border-indigo-200 dark:border-indigo-700'
                              }`}
                              title={msg.workflowTrackLink ? `Open ${msg.workflowTrackId}` : msg.workflowTrackId}
                              aria-label={`Track ID: ${msg.workflowTrackId}`}
                            >
                              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                            </svg>
                              <span className="text-[10px] font-semibold leading-tight">
                                {msg.workflowTrackId}
                              </span>
                                          </button>
                          )}
                          {msg.templateInfo?.estimateTimeInMinutes > 0 && (
                            <span
                              className={`flex items-center gap-1 min-h-6 px-2 py-0.5 rounded-full text-xs border flex-shrink-0 ${
                                !msgBubbleTextOnLight
                                  ? 'bg-white/20 text-white border-white/30'
                                  : 'bg-sky-50 dark:bg-sky-900/30 text-sky-800 dark:text-sky-200 border-sky-200 dark:border-sky-700'
                              }`}
                              title="Estimated time"
                            >
                              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                              <span className="text-[10px] font-semibold leading-tight">
                                Est: {formatEstimateTime(msg.templateInfo.estimateTimeInMinutes)}
                              </span>
                                          </span>
                                        )}
                          {msg.templateInfo?.requiresAcknowledge && (() => {
                            const acks = msg.templateInfo.acknowledgedByUserIds || [];
                            const already = currentUserId && acks.includes(currentUserId);
                            return (
                                    <button
                                type="button"
                                disabled={already}
                                onClick={async (e) => {
                                        e.stopPropagation();
                                  if (already) return;
                                  try {
                                    const updated = await acknowledgeTemplateMessage(msg.id);
                                    upsertRealmDocs('Message', [updated]).catch(() => {});
                                    dispatch(updateMessage({
                                      channelId: updated.channelId || msg.channelId,
                                      message: updated,
                                    }));
                                    dispatch(addNotification({ type: 'success', message: 'Acknowledged' }));
                                  } catch (err) {
                                    dispatch(addNotification({
                                      type: 'error',
                                      message: err?.response?.data?.error || 'Failed to acknowledge',
                                    }));
                                  }
                                }}
                                className={`flex items-center gap-1 min-h-6 px-2 py-0.5 rounded-full text-xs border flex-shrink-0 ${
                                  already
                                    ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-200 border-emerald-200 dark:border-emerald-700'
                                    : 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-700 hover:bg-amber-100'
                                }`}
                                title={already ? 'You acknowledged this' : 'Acknowledge'}
                              >
                                <span className="text-[10px] font-semibold leading-tight">
                                  {already ? `Ack · ${acks.length}` : 'Acknowledge'}
                                </span>
                                    </button>
                            );
                          })()}
                          {msg.workflowAssignedToUserId
                            && currentUserId
                            && String(msg.workflowAssignedToUserId) === String(currentUserId)
                            && !Object.values(getActiveWorkflowByCategory(
                              mergeReactionsWithWorkflowCategory(msg.reactions, msg.workflowByCategory)
                            ) || {}).includes(WORKFLOW_CLOSED_STATUS_ID)
                            && (messageIsRaisedWorkflow(msg.reactions) || findLinkedTaskForMessage(msg)?.id) && (
                            <span onClick={(e) => e.stopPropagation()}>
                              <WorkItemTimer
                                targetType={messageIsRaisedWorkflow(msg.reactions) ? 'WORKFLOW' : 'TASK'}
                                targetId={
                                  messageIsRaisedWorkflow(msg.reactions)
                                    ? (msg.id || msg._id)
                                    : findLinkedTaskForMessage(msg).id
                                }
                                noun={messageIsRaisedWorkflow(msg.reactions) ? 'workflow' : 'task'}
                                compact
                              />
                            </span>
                          )}
                          {messageHasUrgentWorkflow(msg.reactions)
                            && !Object.values(getActiveWorkflowByCategory(
                              mergeReactionsWithWorkflowCategory(msg.reactions, msg.workflowByCategory)
                            ) || {}).includes(WORKFLOW_CLOSED_STATUS_ID)
                            && msg.urgentDeadlineAt && (
                            <span className="inline-flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                              <UrgentTag
                                deadlineAt={msg.urgentDeadlineAt}
                                estimateMinutes={getMessageUrgentEstimateMinutes(msg, allTasks)}
                              />
                              <UrgentCountdownBadge
                                deadlineAt={msg.urgentDeadlineAt}
                                estimateMinutes={getMessageUrgentEstimateMinutes(msg, allTasks)}
                                compact
                              />
                            </span>
                          )}
                          {(msg.customTags || []).map((tag) => (
                            <span
                              key={`tag-${messageKey}-${tag.id}`}
                              className={`group/tag inline-flex items-center gap-0.5 max-w-[9rem] min-h-6 px-2 py-0.5 rounded-full text-xs font-semibold border transition-colors flex-shrink-0 ${jiraTagClasses}`}
                              title={tag.label}
                            >
                              <span className="truncate leading-tight">
                                {searchHighlightQuery?.trim()
                                  ? renderHighlightedSearchText(tag.label, searchHighlightQuery, `tag-${tag.id}`)
                                  : tag.label}
                              </span>
                <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveCustomTag(reactionMessageId, tag.id);
                                }}
                                className="opacity-0 group-hover/tag:opacity-100 flex-shrink-0 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10 transition-opacity"
                                title="Remove tag"
                                aria-label={`Remove tag ${tag.label}`}
                              >
                                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                            </span>
                          ))}
                          {isTagInputOpen ? (
                            <input
                              ref={tagInputMessageKey === messageKey ? tagInputRef : null}
                              type="text"
                              value={tagInputDraft}
                              onChange={(e) => setTagInputDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleSubmitCustomTag(reactionMessageId, messageKey);
                                }
                                if (e.key === 'Escape') {
                                  e.preventDefault();
                                  handleCloseCustomTagInput();
                                }
                              }}
                              onBlur={() => {
                                if (tagInputDraft.trim()) {
                                  handleSubmitCustomTag(reactionMessageId, messageKey);
                                } else {
                                  handleCloseCustomTagInput();
                                }
                              }}
                              placeholder="Add label..."
                              maxLength={64}
                              className={`h-6 w-28 px-2 text-xs rounded-full border focus:outline-none focus:ring-1 flex-shrink-0 ${tagInputClasses}`}
                            />
                          ) : (
                      <button
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleOpenCustomTagInput(messageKey)}
                              className={`inline-flex items-center justify-center w-6 h-6 rounded-full border border-dashed text-xs font-semibold leading-none opacity-0 group-hover/meta:opacity-100 transition-opacity flex-shrink-0 ${addTagBtnClasses}`}
                              title="Add tag"
                              aria-label="Add tag"
                            >
                              +
                      </button>
                )}
              </div>
                      );
                    })()}
                    {(() => {
                      const linkedTask = findLinkedTaskForMessage(msg);
                      if (isWorkTimelineCardMessage(msg)) return null;
                      const isWorkflowBubble = isWorkflowChatBubble(msg);
                      const namedTask = linkedTask
                        && (linkedTask.title || '').trim()
                        && !isWorkflowShellTask(linkedTask)
                        ? linkedTask
                        : null;
                      const isTaskBubble = Boolean(namedTask) && !isWorkflowBubble;
                      const isTaskItem = messageIsTaskWorkItem(msg.reactions)
                        || Boolean(namedTask && !messageIsRaisedWorkflow(msg.reactions));
                      const isRaisedWorkflow = messageIsRaisedWorkflow(msg.reactions);
                      const hasWorkStatus = Boolean(getActiveWorkflowByCategory(
                        mergeReactionsWithWorkflowCategory(msg.reactions || [], msg.workflowByCategory)
                      )['workflow-status']);
                      const showAssign = isRaisedWorkflow || isTaskItem || hasWorkStatus;
                      const showOpenWorkItem = (isWorkflowBubble || Boolean(namedTask))
                        && msg.status !== 'pending'
                        && msg.status !== 'failed'
                        && Boolean(msg.id);
                      // Own row below meta pills — work-item type / Task label, bottom-right.
                      const typeId = getActiveWorkflowByCategory(msg.reactions || [])['work-item-type'];
                      const typeLabel = typeId
                        ? (getReactionDisplay(typeId)?.label || null)
                        : (msgWorkflowVisual?.watermarkLabel || null);
                      const watermarkLabel = isWorkflowBubble
                        ? (typeLabel || 'Workflow')
                        : (isTaskBubble ? 'Task' : null);
                      if (!showOpenWorkItem && !watermarkLabel && !showAssign) return null;
                      const assignee = showAssign ? resolveMessageAssignee(msg, linkedTask) : { id: null, name: '' };
                      return (
                        <div className="relative z-[4] flex items-center justify-between gap-1.5 w-full px-2.5 pb-1.5 pt-0.5">
                          {showAssign ? (
                            <div
                              ref={assignPickerMessageKey === messageKey ? assignPickerRef : null}
                              className="flex-shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <WorkflowAssigneeDropdown
                                layout="person"
                                hideLabel
                                users={assignableUsers}
                                selectedUserId={assignee.id}
                                selectedUserName={assignee.name}
                                isOpen={assignDropdownOpenMessageKey === messageKey}
                                searchQuery={assignSearchQuery}
                                onToggleOpen={(open) => {
                                  setAssignPickerMessageKey(open ? messageKey : null);
                                  setAssignDropdownOpenMessageKey(open ? messageKey : null);
                                  if (!open) setAssignSearchQuery('');
                                }}
                                onSearchChange={setAssignSearchQuery}
                                onSelect={(userId) => handleAssignWorkflow(msg, userId)}
                                variant={!msgBubbleTextOnLight ? 'dark' : 'default'}
                              />
                            </div>
                          ) : <span />}
                          <div className="flex items-center justify-end gap-1.5 min-w-0">
                          {watermarkLabel ? (
                            <span
                              className={`whitespace-nowrap leading-none truncate max-w-[70%] pointer-events-none select-none ${
                                isTaskBubble
                                  ? 'text-sm font-extrabold uppercase tracking-wide text-orange-600 dark:text-orange-300'
                                  : 'text-sm font-extrabold uppercase tracking-wide text-indigo-600 dark:text-indigo-300'
                              }`}
                              title={watermarkLabel}
                            >
                              {watermarkLabel}
                            </span>
                          ) : null}
                          {showOpenWorkItem ? (
                            <button
                              type="button"
                              data-open-work-item
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleOpenWorkItemPopup(msg);
                              }}
                              className={`flex-shrink-0 inline-flex items-center gap-0.5 rounded-md border px-1 py-0.5 shadow-sm transition-colors ${
                                msgBubbleTextOnLight
                                  ? 'bg-white/90 dark:bg-gray-800/90 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-300'
                                  : 'bg-black/35 border-white/20 text-white hover:bg-black/50'
                              }`}
                              title={isTaskBubble ? 'Open task' : 'Open workflow'}
                              aria-label={isTaskBubble ? 'Open task' : 'Open workflow'}
                            >
                              <TaskDocIcon className="w-3.5 h-3.5" />
                              <ChevronRightIcon className="w-3 h-3" />
                            </button>
                          ) : null}
                          </div>
                        </div>
                      );
                    })()}
                    {messageInlineMenu}
                    </div>
                  </MessageSectionShell>
                  {showInlineReplies ? (() => {
                    const uniqueSenders = [];
                    const seen = new Set();
                    threadReplies.forEach((reply) => {
                      const key = String(reply.senderId || reply.senderName || 'unknown');
                      if (!seen.has(key)) {
                        seen.add(key);
                        uniqueSenders.push(reply);
                      }
                    });
                    const singleSender = uniqueSenders.length === 1;
                    const headerReply = uniqueSenders[0] || threadReplies[0];

                    return (
                      <div
                        className={`mt-2 mb-4 flex gap-2.5 w-[90%] min-w-0 max-w-[90%] ${
                          bubbleOwn && !isInThread ? 'self-start' : 'self-end'
                        }`}
                      >
                        <div
                          className="w-0.5 flex-shrink-0 self-stretch rounded-full bg-sky-400 dark:bg-sky-500/80 ml-2"
                          aria-hidden
                        />
                        <div className="min-w-0 flex-1">
                          {singleSender ? (
                            <div className="flex items-center gap-2 mb-1.5">
                              <PresignedAvatar
                                fileKey={headerReply.senderProfilePicture}
                                name={headerReply.senderName}
                                initials={headerReply.senderName ? headerReply.senderName.substring(0, 2).toUpperCase() : 'U'}
                                className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                                fallbackClassName="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0"
                              />
                              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                {headerReply.senderName || 'User'}
                              </span>
                            </div>
                          ) : null}
                          <div className="rounded-2xl bg-gray-100 dark:bg-gray-700/80 overflow-hidden shadow-sm">
                            {threadReplies.map((reply, replyIndex) => {
                              const replyText = getReplyPlainText(reply);
                              const replyKey = reply.id || reply.tempId;
                              const replyTime = formatBubbleTime(reply.createdAt || reply.timestamp);
                              const replyShare = withWorkItemShareTitle(resolveWorkItemShare(reply), {
                                tasks: allTasks,
                                messages: [...allMessages, ...allChatMessages],
                              });
                              const replyThreadShare = !replyShare ? resolveChatThreadShare(reply) : null;
                              const openInlineReply = (e) => {
                                e.stopPropagation();
                                if (replyShare) {
                                  window.dispatchEvent(new CustomEvent('workloop:open-shared-item', { detail: replyShare }));
                                  return;
                                }
                                if (replyThreadShare) {
                                  window.dispatchEvent(new CustomEvent('workloop:go-to-message', {
                                    detail: {
                                      channelId: replyThreadShare.channelId || reply.channelId || msg.channelId || channelIdProp,
                                      messageId: replyThreadShare.messageId,
                                      threadId: replyThreadShare.threadId || undefined,
                                      replyToMessageId: replyThreadShare.threadId && replyThreadShare.threadId !== replyThreadShare.messageId
                                        ? replyThreadShare.threadId
                                        : undefined,
                                      openThread: Boolean(replyThreadShare.threadId),
                                    },
                                  }));
                                  return;
                                }
                                if (onViewThread) onViewThread(msg.id);
                              };
                              return (
                                <div
                                  key={replyKey}
                                  className={`relative group/inline-reply w-full text-left px-3 py-2.5 ${
                                    replyIndex > 0 ? 'border-t border-gray-200/90 dark:border-gray-600/70' : ''
                                  }`}
                                >
                                  {replyShare || replyThreadShare ? (
                                    <WorkItemShareCard
                                      share={replyShare || { title: 'Open in chat' }}
                                      typeLabel={replyShare ? undefined : 'Thread'}
                                      onOpen={openInlineReply}
                                      onCopyLink={() => {
                                        if (replyShare) {
                                          handleCopyShareLink(
                                            buildWorkItemShareUrl(replyShare),
                                            'Link copied',
                                          );
                                          return;
                                        }
                                        handleCopyShareLink(
                                          buildChatThreadShareUrl({
                                            channelId: replyThreadShare?.channelId || reply.channelId || msg.channelId || channelIdProp,
                                            messageId: replyThreadShare?.messageId,
                                            threadId: replyThreadShare?.threadId,
                                          }),
                                          'Thread link copied',
                                        );
                                      }}
                                    />
                                  ) : (
                                  <button
                                    type="button"
                                    onClick={openInlineReply}
                                    className="w-full text-left grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-2 -mx-3 -my-2.5 px-3 py-2.5 hover:bg-black/[0.03] dark:hover:bg-white/[0.04] transition-colors"
                                    title="Open reply thread"
                                  >
                                    <span className="min-w-0 text-sm leading-snug text-gray-900 dark:text-gray-100 break-words">
                                      {!singleSender ? (
                                        <>
                                          <span className="font-semibold">
                                            {reply.senderName || 'User'}
                                          </span>
                                          <span className="text-gray-400 dark:text-gray-500"> · </span>
                                        </>
                                      ) : null}
                                      {replyText}
                                    </span>
                                    {replyTime ? (
                                      <span className="shrink-0 text-[10px] leading-none tabular-nums whitespace-nowrap text-gray-400 dark:text-gray-500">
                                        {replyTime}
                                      </span>
                                    ) : null}
                                  </button>
                                  )}
                                  {!(replyShare || replyThreadShare) && reply.id && reply.status !== 'pending' && reply.status !== 'failed' ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyThreadLink(reply);
                                      }}
                                      className="absolute top-1.5 right-1.5 z-10 p-1 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white/80 dark:hover:bg-gray-800/80 opacity-0 group-hover/inline-reply:opacity-100 focus:opacity-100 transition-opacity"
                                      title="Copy thread link"
                                      aria-label="Copy thread link"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                      </svg>
                                    </button>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })() : null}
                  {hasThreadReplies && !showInlineReplies ? (() => {
                    const uniqueParticipants = [];
                    const seenSenders = new Set();
                    for (const reply of threadReplies) {
                      const identifier = reply.senderId || reply.senderName || 'unknown';
                      if (!seenSenders.has(identifier)) {
                        seenSenders.add(identifier);
                        uniqueParticipants.push(reply);
                      }
                    }

                    const participantNames = uniqueParticipants
                      .map((p) => p.senderName || 'Unknown User')
                      .filter(Boolean)
                      .join(', ');

                    const lastReply = threadReplies[threadReplies.length - 1];
                    const lastReplyTimeStr = lastReply
                      ? formatTime(lastReply.createdAt || lastReply.timestamp)
                      : '';

                    return (
              <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onViewThread) onViewThread(msg.id);
                        }}
                        title={participantNames ? `Replies from: ${participantNames}` : ''}
                        className={`mt-2 flex items-center gap-2.5 min-w-0 max-w-full text-left rounded-md px-1 py-1 hover:bg-gray-100 dark:hover:bg-gray-800/80 transition-colors ${
                          bubbleOwn && !isInThread ? 'self-start' : 'self-end'
                        }`}
                      >
                        <div className="flex -space-x-2 flex-shrink-0">
                          {uniqueParticipants.slice(0, 3).map((participant, i) => (
                            <PresignedAvatar
                              key={i}
                              fileKey={participant.senderProfilePicture}
                              name={participant.senderName}
                              initials={participant.senderName ? participant.senderName.substring(0, 2).toUpperCase() : 'U'}
                              className="w-7 h-7 rounded-full border-2 border-white dark:border-gray-800 object-cover"
                              fallbackClassName="w-7 h-7 bg-primary-500 rounded-full flex items-center justify-center text-white text-[10px] font-semibold border-2 border-white dark:border-gray-800"
                            />
                          ))}
                          {uniqueParticipants.length > 3 && (
                            <div className="w-7 h-7 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-full border-2 border-white dark:border-gray-800 flex items-center justify-center text-[10px] font-bold flex-shrink-0 z-10">
                              +{uniqueParticipants.length - 3}
                </div>
              )}
            </div>
                        <span className="text-[15px] font-semibold text-sky-600 dark:text-sky-400 whitespace-nowrap">
                          {threadReplies.length} {threadReplies.length === 1 ? 'reply' : 'replies'}
                        </span>
                        {lastReplyTimeStr ? (
                          <span className="text-[13px] text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">
                            Last reply {lastReplyTimeStr}
                          </span>
                        ) : null}
            </button>
                    );
                  })() : null}
          </div>
                );
              })}
          </div>

          {reactionTargetMessage && showReactions && reactionPickerPortalPosition && ReactDOM.createPortal(
            <div
              ref={reactionPickerPortalRef}
              style={{
                position: 'fixed',
                top: reactionPickerPortalPosition.top,
                left: reactionPickerPortalPosition.left,
                zIndex: REACTION_PICKER_Z_INDEX,
              }}
            >
              <ReactionPicker
                onSelect={(reactionId) => handleReactionClick(reactionTargetMessage.id || reactionTargetMessage.tempId, reactionId)}
                currentReactions={reactionTargetMessage.reactions || []}
                currentUserId={currentUserId}
              />
            </div>,
            document.body
          )}

          </div>

      </div>

      {/* Image gallery modal */}
      {viewingGallery && (
        <ImageViewerModal
          images={viewingGallery.images}
          initialIndex={viewingGallery.index}
          onClose={() => setViewingGallery(null)}
          onDownload={handleDownload}
        />
      )}

      {viewingGroup && (
        <AttachmentComposeModal
          attachments={viewingGroup.attachments}
          groupName={viewingGroup.groupName}
          initialIndex={viewingGroup.index}
          readOnly
          onClose={() => setViewingGroup(null)}
        />
      )}

      {/* All attachments list modal */}
      {viewingAllAttachments && (
        <AttachmentsListModal
          attachments={viewingAllAttachments}
          onClose={() => setViewingAllAttachments(null)}
          onViewImage={(images, index) => {
            setViewingAllAttachments(null);
            setViewingGallery({ images, index });
          }}
          onDownload={handleDownload}
        />
      )}

      {/* User Profile Modal */}
      {profileUserId && (
        <UserProfileModal
          userId={profileUserId}
          isOpen={Boolean(profileUserId)}
          onClose={() => setProfileUserId(null)}
        />
      )}

      <SensitiveUnlockModal
        open={Boolean(sensitiveUnlockMessage)}
        message={sensitiveUnlockMessage}
        onClose={() => setSensitiveUnlockMessage(null)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setMessageToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Message"
        message="Are you sure you want to delete this message? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Create Note Dialog */}
      <CreateNoteDialog
        isOpen={showNoteDialog}
        onClose={() => {
          setShowNoteDialog(false);
          setNoteInitialDescription('');
          setNoteMessageId(null);
        }}
        initialDescription={noteInitialDescription}
        messageId={noteMessageId}
        onSave={(noteData) => {
          // Handle note creation here
          // You can add API call or callback here
          console.log('Creating note:', noteData);

          // TODO: Add API call to save the note
          // Example:
          // await saveNote(noteData);
        }}
      />

      {/* Workflow Track ID Dialog */}
      <WorkflowTrackDialog
        isOpen={showTrackDialog}
        onClose={() => {
          setShowTrackDialog(false);
          setTrackDialogMessage(null);
        }}
        initialTrackId={trackDialogMessage?.workflowTrackId || ''}
        initialTrackLink={trackDialogMessage?.workflowTrackLink || ''}
        messageId={trackDialogMessage?.id || trackDialogMessage?.tempId}
        onSave={handleSaveWorkflowTrack}
      />

      {viewingWorkflow && (
        <WorkflowViewDialog
          item={viewingWorkflow}
          onClose={() => setViewingWorkflow(null)}
          onViewImage={(images, index) => setViewingGallery({ images, index })}
          onDownload={handleDownload}
        />
      )}
      {viewingLinkedTask && (
        <TaskViewDialog
          task={viewingLinkedTask}
          onClose={() => setViewingLinkedTask(null)}
          usePortal
          zIndexClass="z-[1000]"
          onNavigateToMessage={(navTask) => {
            setViewingLinkedTask(null);
            const messageId = String(navTask?.messageId || '').trim();
            if (!messageId) return;
            const channelId = navTask?.channelId || message?.channelId || messages?.[0]?.channelId;
            if (onScrollToMessage && (!channelId || String(channelId) === String(message?.channelId || messages?.[0]?.channelId))) {
              onScrollToMessage(messageId, {
                replyToMessageId: navTask?.replyToMessageId,
              });
              return;
            }
            window.dispatchEvent(new CustomEvent('workloop:go-to-message', {
              detail: {
                messageId,
                channelId,
                replyToMessageId: navTask?.replyToMessageId || null,
              },
            }));
          }}
          onOpenWorkflow={(item) => {
            setViewingLinkedTask(null);
            if (item?.messageId) setViewingWorkflow(item);
          }}
        />
      )}

      {/* Task Creation Dialog */}
      <TaskCreationDialog
        isOpen={showTaskDialog}
        onClose={() => {
          setShowTaskDialog(false);
          setTaskInitialDescription('');
          setTaskMessageId(null);
          setTaskMapTaskId(null);
        }}
        initialDescription={taskInitialDescription}
        messageId={taskMessageId}
        mapTaskId={taskMapTaskId}
        defaultSpaceId={channelSpace.spaceId}
        defaultSpaceName={channelSpace.spaceName}
        onSaved={() => {
          window.dispatchEvent(new CustomEvent('workloop:stick-timeline-to-latest', {
            detail: { messageId: taskMessageId },
          }));
        }}
      />
      {/* Task Mapping Dialog */}
      <TaskMappingDialog
        isOpen={showMapTaskDialog}
        onClose={() => {
          // Only clear state when dialog is cancelled (not when task is selected)
          setShowMapTaskDialog(false);
          // Don't clear taskInitialDescription and taskMessageId here
          // They will be cleared when task creation dialog closes
        }}
        onTaskSelected={handleTaskSelected}
      />
      <SaveToVaultModal
        open={Boolean(vaultSaveMessage)}
        defaultTitle={vaultSaveMessage ? vaultDefaultTitle(vaultSaveMessage) : ''}
        message={vaultSaveMessage}
        saving={vaultSaving}
        onClose={() => {
          if (!vaultSaving) setVaultSaveMessage(null);
        }}
        onSave={handleConfirmSaveToVault}
      />
    </div>
  );
};

export default MessageItem;

